import { z } from "zod";
import { isValidIsbn, normalizeIsbn } from "./isbn";
import { moduleKeys } from "./modules";

function isSupportedTimeZone(value: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

export const roleSchema = z.enum(["administrator", "manager", "staff"]);

export const moduleKeySchema = z.enum(moduleKeys as [typeof moduleKeys[number], ...typeof moduleKeys[number][]]);

export const moduleUpdateSchema = z.object({
  enabled: z.boolean(),
  reason: z.string().trim().max(500).optional().nullable().transform((value) => value || null),
  enableDependencies: z.boolean().default(false),
  config: z.record(z.unknown()).optional(),
});

export const reportPresetRangeSchema = z.enum(["today", "yesterday", "last_7_days", "last_30_days", "this_month", "last_month", "this_quarter", "this_year", "previous_year", "custom"]);
export const productTypeSchema = z.enum(["book", "movie", "music", "game", "merchandise", "other"]);
export const reportProductTypeSchema = z.enum(["all", "book", "movie", "music", "game", "merchandise", "other"]);

export const reportFilterSchema = z.object({
  preset: reportPresetRangeSchema.default("last_30_days"),
  startDate: z.string().trim().datetime().optional(),
  endDate: z.string().trim().datetime().optional(),
  comparison: z.enum(["none", "previous_equivalent_period", "previous_month", "previous_year"]).default("none"),
  locationId: z.string().trim().max(120).optional(),
  registerId: z.string().trim().max(120).optional(),
  employeeId: z.string().trim().max(120).optional(),
  channel: z.enum(["all", "in_store", "online", "reservation", "other"]).default("all"),
  productType: reportProductTypeSchema.default("all"),
  newUsed: z.enum(["all", "new", "used"]).default("all"),
  condition: z.string().trim().max(80).optional(),
  author: z.string().trim().max(160).optional(),
  publisher: z.string().trim().max(160).optional(),
  categoryId: z.string().trim().max(120).optional(),
  supplierName: z.string().trim().max(160).optional(),
  paymentMethod: z.string().trim().max(80).optional(),
  taxStatus: z.enum(["all", "taxed", "untaxed"]).default("all"),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

export const loyaltyProgramUpdateSchema = z.object({
  enabled: z.boolean().default(false),
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(1000).optional().nullable().transform((value) => value || null),
  termsText: z.string().trim().max(4000).optional().nullable().transform((value) => value || null),
  pointsPerCurrencyAmount: z.number().int().min(0).max(1000).default(1),
  currencyAmount: z.number().int().positive().max(100000).default(100),
  minimumPurchaseAmount: z.number().int().min(0).max(1000000).default(0),
  taxEarnsPoints: z.boolean().default(false),
  shippingEarnsPoints: z.boolean().default(false),
  giftCardPurchasesEarnPoints: z.boolean().default(false),
  giftCardRedemptionsEarnPoints: z.boolean().default(false),
  discountedItemsEarnPoints: z.boolean().default(true),
  usedBooksPointsMultiplierBasisPoints: z.number().int().min(0).max(100000).default(10000),
  newBooksPointsMultiplierBasisPoints: z.number().int().min(0).max(100000).default(10000),
  merchandiseEarnsPoints: z.boolean().default(true),
  refundReversesPoints: z.boolean().default(true),
  autoEnrollCustomers: z.boolean().default(false),
  pointsExpireAfterDays: z.number().int().positive().max(3650).optional().nullable().transform((value) => value ?? null),
});

export const loyaltyEnrollmentSchema = z.object({
  customerId: z.string().trim().min(1),
  termsAccepted: z.boolean().default(false),
  source: z.enum(["staff", "checkout", "storefront"]).default("staff"),
  idempotencyKey: z.string().min(8),
});

export const loyaltyAdjustmentSchema = z.object({
  customerId: z.string().trim().min(1),
  pointsDelta: z.number().int().refine((value) => value !== 0, "Adjustment must change the balance"),
  reason: z.string().trim().min(3).max(500),
  idempotencyKey: z.string().min(8),
});

export const loyaltyRedemptionSchema = z.object({
  customerId: z.string().trim().min(1),
  rewardDefinitionId: z.string().trim().min(1),
  saleId: z.string().trim().max(120).optional(),
  idempotencyKey: z.string().min(8),
});

export const loyaltyRedemptionReversalSchema = z.object({
  reason: z.string().trim().min(3).max(500),
  idempotencyKey: z.string().min(8),
});

export const loyaltyRewardDefinitionSaveSchema = z.object({
  id: z.string().trim().min(1).max(120).optional(),
  name: z.string().trim().min(1).max(160),
  rewardType: z.enum(["store_credit", "fixed_discount"]),
  pointsCost: z.number().int().min(1).max(1000000),
  rewardAmount: z.number().int().min(1).max(1000000),
  currency: z.string().trim().length(3).default("USD"),
  minimumPurchaseAmount: z.number().int().min(0).max(1000000).default(0),
  active: z.boolean().default(true),
});

export const loyaltyLevelSaveSchema = z.object({
  id: z.string().trim().min(1).max(120).optional(),
  name: z.string().trim().min(1).max(120),
  thresholdPoints: z.number().int().min(0).max(10000000),
  thresholdSpendAmount: z.number().int().min(0).max(100000000).default(0),
  benefitsText: z.string().trim().max(1000).optional().nullable().transform((value) => value || undefined),
  active: z.boolean().default(true),
  displayOrder: z.number().int().min(0).max(10000).default(0),
});

const monthDaySchema = z.string().regex(/^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/, "Use MM-DD").optional();

export const loyaltyCampaignSaveSchema = z.object({
  id: z.string().trim().min(1).optional(),
  name: z.string().trim().min(1).max(160),
  description: z.string().trim().max(1000).optional().nullable().transform((value) => value || undefined),
  campaignType: z.enum(["bonus_points", "points_multiplier", "birthday_bonus", "anniversary_bonus", "buy_n_books"]),
  enabled: z.boolean().default(true),
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime().optional().nullable().transform((value) => value || undefined),
  priority: z.number().int().min(0).max(1000).default(0),
  stackable: z.boolean().default(false),
  minimumPurchaseAmount: z.number().int().min(0).max(1000000).default(0),
  bonusPoints: z.number().int().min(0).max(1000000).optional(),
  pointsMultiplierBasisPoints: z.number().int().min(0).max(100000).optional(),
  eligibility: z.record(z.string(), z.unknown()).default({}),
  expirationDate: z.string().datetime().optional().nullable().transform((value) => value || undefined),
  rewardExpiresAfterDays: z.number().int().positive().max(3650).optional().nullable().transform((value) => value ?? undefined),
}).superRefine((value, ctx) => {
  if (value.endsAt && value.endsAt <= value.startsAt) ctx.addIssue({ code: "custom", path: ["endsAt"], message: "End time must be after start time" });
  if (value.campaignType === "bonus_points" || value.campaignType === "birthday_bonus" || value.campaignType === "anniversary_bonus" || value.campaignType === "buy_n_books") {
    if (!value.bonusPoints || value.bonusPoints <= 0) ctx.addIssue({ code: "custom", path: ["bonusPoints"], message: "Bonus campaigns require positive bonus points" });
  }
  if (value.campaignType === "buy_n_books") {
    const requiredQuantity = Number((value.eligibility as { requiredQuantity?: unknown }).requiredQuantity ?? 0);
    if (!Number.isInteger(requiredQuantity) || requiredQuantity < 1 || requiredQuantity > 100) {
      ctx.addIssue({ code: "custom", path: ["eligibility", "requiredQuantity"], message: "Buy-N campaigns require a qualifying book count from 1 to 100" });
    }
  }
  if (value.campaignType === "points_multiplier" && (!value.pointsMultiplierBasisPoints || value.pointsMultiplierBasisPoints <= 10000)) {
    ctx.addIssue({ code: "custom", path: ["pointsMultiplierBasisPoints"], message: "Multiplier campaigns require a multiplier greater than 1x" });
  }
});

export const loyaltyExpirationProcessSchema = z.object({
  now: z.string().datetime().optional(),
  limit: z.number().int().min(1).max(500).default(100),
  idempotencyKey: z.string().min(8).optional(),
});

export const storefrontSettingsUpdateSchema = z.object({
  enabled: z.boolean().default(true),
  displayName: z.string().trim().min(1).max(160),
  pickupLocationId: z.string().trim().min(1).max(120),
  contactEmail: z.string().trim().email().optional().nullable().transform((value) => value || null),
  contactPhone: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  pickupInstructions: z.string().trim().max(1000).optional().nullable().transform((value) => value || null),
  announcementText: z.string().trim().max(500).optional().nullable().transform((value) => value || null),
  announcementEnabled: z.boolean().default(false),
  homepageSections: z.array(z.enum(["hero", "collections", "staff_picks", "popular", "genre_shelves", "signed", "catalog", "store_info", "loyalty"])).max(12).default(["collections", "staff_picks", "catalog"]),
  themePreset: z.enum(["bookshop", "modern", "classic", "bright"]).default("bookshop"),
  colorPrimary: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional().nullable(),
  colorAccent: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional().nullable(),
  colorBackground: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional().nullable(),
  colorSurface: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional().nullable(),
  colorText: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional().nullable(),
  heroStyle: z.enum(["immersive", "compact", "minimal"]).default("immersive"),
  catalogLayout: z.enum(["grid", "list"]).default("grid"),
  gridDensity: z.enum(["compact", "balanced", "spacious"]).default("balanced"),
  cardStyle: z.enum(["detailed", "cover", "compact"]).default("detailed"),
  headingFont: z.enum(["modern_sans", "bookish_serif", "classic_serif", "friendly_rounded"]).optional().nullable(),
  bodyFont: z.enum(["clean_sans", "humanist_sans", "literary_serif"]).optional().nullable(),
  sectionLayouts: z.record(z.enum(["grid", "shelf", "list"])).default({}),
  reservationDurationMinutes: z.number().int().min(1).max(120).default(15),
  reconciliationHoldMinutes: z.number().int().min(5).max(240).default(30),
  pickupPolicyText: z.string().trim().max(2000).optional().nullable().transform((value) => value || null),
  returnPolicyText: z.string().trim().max(3000).optional().nullable().transform((value) => value || null),
  privacyPolicyText: z.string().trim().max(3000).optional().nullable().transform((value) => value || null),
  termsPolicyText: z.string().trim().max(3000).optional().nullable().transform((value) => value || null),
  shippingEnabled: z.boolean().default(false),
  flatRateShippingAmount: z.number().int().min(0).max(100000).default(0),
  freeShippingThresholdAmount: z.number().int().min(0).max(1000000).optional().nullable(),
  shippingPolicyText: z.string().trim().max(3000).optional().nullable().transform((value) => value || null),
  handlingTimeDays: z.number().int().min(0).max(30).default(2),
  originAddress: z.record(z.unknown()).default({}),
  allowedDestinationRegions: z.array(z.string().trim().min(2).max(32)).max(100).default([]),
  defaultPackageWeightGrams: z.number().int().min(1).max(50000).default(454),
  defaultPackageDimensions: z.record(z.unknown()).default({}),
  usedBooksShippable: z.boolean().default(true),
  rareItemsRequireApproval: z.boolean().default(false),
});

export const storefrontTenantUpdateSchema = z.object({
  slug: z.string().trim().min(1).max(63).optional(),
  enabled: z.boolean().optional(),
  canonicalHost: z.string().trim().max(253).optional().nullable().transform((value) => value || null),
});

export const storefrontCustomDomainCreateSchema = z.object({
  hostname: z.string().trim().min(4).max(253),
});

export const storefrontCustomDomainVerifySchema = z.object({
  observedToken: z.string().trim().min(8).max(160).optional(),
});

export const storefrontCollectionSaveSchema = z.object({
  id: z.string().trim().min(1).optional(),
  name: z.string().trim().min(1).max(120),
  slug: z.string().trim().min(1).max(120).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  description: z.string().trim().max(1000).optional().nullable().transform((value) => value || null),
  imageUrl: z.string().trim().url().optional().nullable().transform((value) => value || null),
  publicStatus: z.enum(["public", "hidden"]).default("public"),
  featured: z.boolean().default(false),
  startsAt: z.string().trim().datetime().optional().nullable().transform((value) => value || null),
  endsAt: z.string().trim().datetime().optional().nullable().transform((value) => value || null),
  sortOrder: z.number().int().min(0).max(100000).default(0),
  rule: z.object({
    query: z.string().trim().max(120).optional(),
    categoryId: z.string().trim().max(120).optional(),
    author: z.string().trim().max(160).optional(),
    publisher: z.string().trim().max(160).optional(),
    condition: z.string().trim().max(80).optional(),
    signedOnly: z.boolean().optional(),
    minPrice: z.number().int().min(0).optional(),
    maxPrice: z.number().int().min(0).optional(),
    sort: z.enum(["relevance", "newest", "price_asc", "price_desc", "title", "author"]).optional(),
    limit: z.number().int().min(1).max(24).optional(),
  }).optional(),
  variationIds: z.array(z.string().trim().min(1)).max(200).default([]),
  idempotencyKey: z.string().min(8),
});

export const storefrontStaffPickSaveSchema = z.object({
  variationId: z.string().trim().min(1),
  staffDisplayName: z.string().trim().min(1).max(120),
  recommendationText: z.string().trim().min(1).max(1000),
  featured: z.boolean().default(false),
  publicStatus: z.enum(["public", "hidden"]).default("public"),
  idempotencyKey: z.string().min(8),
});

export const customerAccountLookupSchema = z.object({
  emailAddress: z.string().trim().email().optional(),
  phoneNumber: z.string().trim().min(7).max(40).optional(),
}).refine((value) => value.emailAddress || value.phoneNumber, "Provide email or phone");

export const customerLoginRequestSchema = z.object({
  emailAddress: z.string().trim().email().optional(),
  phoneNumber: z.string().trim().min(7).max(40).optional(),
  displayName: z.string().trim().max(120).optional(),
}).refine((value) => value.emailAddress || value.phoneNumber, "Provide email or phone");

export const customerLoginVerifySchema = z.object({
  emailAddress: z.string().trim().email().optional(),
  phoneNumber: z.string().trim().min(7).max(40).optional(),
  code: z.string().trim().regex(/^\d{6}$/, "Enter the 6-digit code"),
}).refine((value) => value.emailAddress || value.phoneNumber, "Provide email or phone");

export const customerCommunicationPreferencesSchema = z.object({
  emailMarketingOptIn: z.boolean().default(false),
  smsMarketingOptIn: z.boolean().default(false),
  availabilityNotificationsOptIn: z.boolean().default(false),
});

export const wishlistMutationSchema = z.object({
  customerId: z.string().trim().min(1),
  variationId: z.string().trim().min(1),
  idempotencyKey: z.string().min(8).optional(),
});

export const subscriptionUpdateSchema = z.object({
  billingStatus: z.enum(["active", "trial", "pending_setup", "past_due", "suspended", "canceled", "grandfathered", "internal_demo"]),
  monthlyPriceAmount: z.number().int().min(0).default(17900),
  setupFeeAmount: z.number().int().min(0).default(9900),
  currency: z.string().length(3).default("USD"),
  subscriptionStart: z.string().datetime().optional().nullable().transform((value) => value || null),
  trialEnd: z.string().datetime().optional().nullable().transform((value) => value || null),
  cancellationDate: z.string().datetime().optional().nullable().transform((value) => value || null),
  billingProviderIdentifier: z.string().trim().max(200).optional().nullable().transform((value) => value || null),
  billingNotes: z.string().trim().max(2000).optional().nullable().transform((value) => value || null),
  grandfatheredPriceAmount: z.number().int().min(0).optional().nullable().transform((value) => value ?? null),
  manualBillingOverride: z.boolean().default(false),
  setupFeeStatus: z.enum(["unpaid", "pending", "paid", "waived"]).default("unpaid"),
  idempotencyKey: z.string().min(8),
});

export const onboardingStepUpdateSchema = z.object({
  status: z.enum(["not_started", "in_progress", "complete", "skipped", "blocked"]),
  notes: z.string().trim().max(1000).optional().nullable().transform((value) => value || null),
  responsibleUserId: z.string().trim().max(120).optional().nullable().transform((value) => value || null),
  idempotencyKey: z.string().min(8),
});

export const loginSchema = z.object({
  username: z.string().trim().min(1),
  password: z.string().min(1),
});

export const demoAccessRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  organization: z.string().trim().min(1).max(160),
  email: z.string().trim().email().max(254),
  phone: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  evaluationNotes: z.string().trim().max(1000).optional().nullable().transform((value) => value || null),
  website: z.string().trim().max(200).optional().default(""),
});

export const supportIssueCreateSchema = z.object({
  title: z.string().trim().min(3).max(180),
  description: z.string().trim().min(5).max(8000),
  severity: z.enum(["low", "normal", "high", "urgent"]).default("normal"),
  attachment: z.object({
    name: z.string().trim().min(1).max(160),
    type: z.enum(["image/png", "image/jpeg", "image/webp"]),
    size: z.number().int().min(1).max(750000),
    dataUrl: z.string().regex(/^data:image\/(png|jpeg|webp);base64,[a-z0-9+/=]+$/i).max(1000000),
  }).optional(),
  context: z.record(z.unknown()).default({}),
  idempotencyKey: z.string().min(8),
});

export const passwordChangeSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(12),
});

export const inventoryQuerySchema = z.object({
  q: z.string().trim().optional(),
  categoryId: z.string().trim().optional(),
  locationId: z.string().trim().optional(),
  stockStatus: z.enum(["in_stock", "low_stock", "out_of_stock", "not_tracked"]).optional(),
  itemType: reportProductTypeSchema.default("all"),
  active: z.enum(["active", "archived", "all"]).default("active"),
  sort: z.enum(["title", "author", "category", "stock", "updated"]).default("title"),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(1000),
});

export const bookLookupSchema = z.object({
  isbn: z.string().transform(normalizeIsbn).refine(isValidIsbn, "Invalid ISBN").optional(),
  title: z.string().trim().min(1).optional(),
  author: z.string().trim().optional(),
  keywords: z.string().trim().optional(),
}).refine((value) => value.isbn || value.title || value.keywords, "Provide ISBN, title, or keywords");

export const productLookupSchema = z.object({
  barcode: z.string().trim().min(3).max(80).optional(),
  title: z.string().trim().min(1).optional(),
  keywords: z.string().trim().min(1).optional(),
}).refine((value) => value.barcode || value.title || value.keywords, "Provide a barcode, title, or keywords");

export const moneySchema = z.object({
  amount: z.number().int().min(0),
  currency: z.string().length(3),
});

export const bookMetadataSchema = z.object({
  source: z.enum(["google_books", "open_library", "upcitemdb"]),
  sourceId: z.string(),
  confidence: z.enum(["exact_isbn", "approximate"]),
  productType: productTypeSchema.optional(),
  barcode: z.string().trim().max(80).optional(),
  isbn10: z.string().optional(),
  isbn13: z.string().optional(),
  title: z.string().min(1),
  subtitle: z.string().optional(),
  authors: z.array(z.string()).default([]),
  description: z.string().optional(),
  publisher: z.string().optional(),
  publicationDate: z.string().optional(),
  categories: z.array(z.string()).default([]),
  pageCount: z.number().int().positive().optional(),
  language: z.string().optional(),
  coverImageUrls: z.array(z.string().url()).default([]),
  format: z.string().optional(),
  externalLinks: z.array(z.object({
    provider: z.string().trim().min(1).max(80),
    label: z.string().trim().min(1).max(120),
    url: z.string().url(),
  })).default([]).optional(),
});

export const defaultSupplierSearchProviders = [
  { id: "google_shopping", name: "Google Shopping", enabled: true, mode: "serpapi_google_shopping", providerType: "marketplace", capabilities: ["open_external_listing"], credentialRequirements: ["SERPAPI_API_KEY"], urlTemplate: "https://www.google.com/search?tbm=shop&q={query}" },
  { id: "ebay", name: "eBay", enabled: true, mode: "ebay_api", providerType: "marketplace", capabilities: ["open_external_listing"], credentialRequirements: ["EBAY_CLIENT_ID", "EBAY_CLIENT_SECRET", "EBAY_BEARER_TOKEN"], urlTemplate: "https://www.ebay.com/sch/i.html?_nkw={query}" },
  { id: "google_books", name: "Google Books", enabled: true, mode: "google_books", providerType: "marketplace", capabilities: ["open_external_listing"], credentialRequirements: ["GOOGLE_BOOKS_API_KEY"], urlTemplate: "https://books.google.com/books?q={query}" },
  { id: "paperbackos_wholesale_fixture", name: "PaperbackOS wholesale fixture", enabled: false, mode: "wholesale_fixture", providerType: "wholesale", capabilities: ["add_to_draft_po", "add_to_existing_po", "create_po", "attach_customer_special_order", "create_customer_special_order", "manual_refresh_availability"], credentialRequirements: ["development fixture only"], urlTemplate: "https://example.com/wholesale?q={query}" },
  { id: "pubeasy", name: "Pubeasy", enabled: false, mode: "pubeasy_soap", providerType: "wholesale", capabilities: ["add_to_draft_po", "add_to_existing_po", "create_po", "attach_customer_special_order", "create_customer_special_order", "direct_to_customer", "manual_refresh_availability"], credentialRequirements: ["PUBEASY_ACCOUNT", "PUBEASY_SOAP_CREDENTIAL"], urlTemplate: "https://www.pubeasy.com/search?isbn={isbn}" },
  { id: "ingram_ipage", name: "Ingram ipage", enabled: false, mode: "ingram_ipage", providerType: "wholesale", capabilities: ["add_to_draft_po", "add_to_existing_po", "create_po", "attach_customer_special_order", "create_customer_special_order", "direct_to_customer", "manual_refresh_availability"], credentialRequirements: ["INGRAM_IPAGE_ACCOUNT", "INGRAM_API_CREDENTIAL"], urlTemplate: "https://ipage.ingramcontent.com/ipage/search?isbn={isbn}" },
  { id: "hpb", name: "Half Price Books", enabled: true, mode: "link", providerType: "marketplace", capabilities: ["open_external_listing"], credentialRequirements: [], urlTemplate: "https://www.hpb.com/search?q={query}" },
  { id: "amazon", name: "Amazon Books", enabled: true, mode: "link", providerType: "marketplace", capabilities: ["open_external_listing"], credentialRequirements: [], urlTemplate: "https://www.amazon.com/s?k={query}&i=stripbooks" },
  { id: "abebooks", name: "AbeBooks", enabled: true, mode: "link", providerType: "marketplace", capabilities: ["open_external_listing"], credentialRequirements: [], urlTemplate: "https://www.abebooks.com/servlet/SearchResults?kn={query}" },
  { id: "thriftbooks", name: "ThriftBooks", enabled: true, mode: "link", providerType: "marketplace", capabilities: ["open_external_listing"], credentialRequirements: [], urlTemplate: "https://www.thriftbooks.com/browse/?b.search={query}" },
  { id: "bookshop", name: "Bookshop.org", enabled: true, mode: "link", providerType: "marketplace", capabilities: ["open_external_listing"], credentialRequirements: [], urlTemplate: "https://bookshop.org/search?keywords={query}" },
] as const;

export const supplierSearchProviderSchema = z.object({
  id: z.string().trim().regex(/^[a-z0-9_-]+$/).max(50),
  name: z.string().trim().min(1).max(80),
  enabled: z.boolean().default(true),
  mode: z.enum(["link", "ebay_api", "google_books", "serpapi_google_shopping", "wholesale_fixture", "wholesale_recorded_fixture", "pubeasy_soap", "ingram_ipage", "direct_supplier_api"]).default("link"),
  providerType: z.enum(["local_inventory", "incoming_inventory", "wholesale", "marketplace", "metadata"]).default("marketplace"),
  capabilities: z.array(z.enum(["sell_local_copy", "reserve_copy", "transfer_location", "view_reservation", "view_open_po", "reserve_incoming", "add_to_draft_po", "add_to_existing_po", "create_po", "attach_customer_special_order", "create_customer_special_order", "direct_to_customer", "confirm_substitution", "manual_refresh_availability", "open_external_listing"])).default(["open_external_listing"]),
  credentialRequirements: z.array(z.string().trim().min(1).max(80)).default([]),
  urlTemplate: z.string().trim().min(1).max(500)
    .refine((value) => value.startsWith("https://"), "Supplier search URLs must use HTTPS")
    .refine((value) => value.includes("{query}") || value.includes("{isbn}") || value.includes("{title}") || value.includes("{author}"), "Supplier URL must include a query placeholder"),
});

export const supplierSearchSchema = z.object({
  itemId: z.string().trim().min(1).max(120).optional(),
  isbn: z.string().transform(normalizeIsbn).optional(),
  title: z.string().trim().optional(),
  author: z.string().trim().optional(),
  manualRefresh: z.boolean().default(false),
  vendorAccountId: z.string().trim().max(160).optional(),
}).refine((value) => value.isbn || value.title, "Provide an ISBN or title");

export const wholesaleRecommendationRulesSchema = z.object({
  preferredVendorWeight: z.number().int().min(0).max(100).default(25),
  netCostWeight: z.number().int().min(0).max(100).default(20),
  arrivalWeight: z.number().int().min(0).max(100).default(15),
  availabilityWeight: z.number().int().min(0).max(100).default(20),
  returnabilityWeight: z.number().int().min(0).max(100).default(10),
  freightWeight: z.number().int().min(0).max(100).default(10),
  preferExistingOpenPo: z.boolean().default(true),
});

export const findStockPoActionSchema = z.object({
  purchaseOrderId: z.string().trim().min(1).max(160).optional(),
  vendorId: z.string().trim().min(1).max(160),
  vendorAccountId: z.string().trim().max(160).optional().nullable().transform((value) => value || null),
  vendorProductId: z.string().trim().max(160).optional().nullable().transform((value) => value || null),
  editionId: z.string().trim().max(160).optional().nullable().transform((value) => value || null),
  variationId: z.string().trim().max(160).optional().nullable().transform((value) => value || null),
  isbn13: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid ISBN-13").optional().nullable().transform((value) => value || null),
  isbn10: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid ISBN-10").optional().nullable().transform((value) => value || null),
  upcEanGtin: z.string().trim().max(80).optional().nullable().transform((value) => value || null),
  internalSku: z.string().trim().max(120).optional().nullable().transform((value) => value || null),
  vendorSku: z.string().trim().max(160).optional().nullable().transform((value) => value || null),
  titleSnapshot: z.string().trim().min(1).max(400),
  quantity: z.number().int().positive().max(100000).default(1),
  expectedUnitCostAmount: z.number().int().min(0).default(0),
  currency: z.string().trim().length(3).default("USD"),
  sourceDemand: z.array(z.record(z.unknown())).default([]),
  substitutionOffered: z.boolean().default(false),
  substitutionConfirmed: z.boolean().default(false),
  directToCustomer: z.boolean().default(false),
  idempotencyKey: z.string().min(8),
}).refine((value) => !value.substitutionOffered || value.substitutionConfirmed, "Confirm substitutes or alternate editions before creating a PO line");

export const findStockSpecialOrderActionSchema = z.object({
  customerId: z.string().trim().min(1).max(160),
  title: z.string().trim().min(1).max(400),
  author: z.string().trim().max(200).optional().nullable().transform((value) => value || null),
  isbn: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid ISBN").optional().nullable().transform((value) => value || null),
  variationId: z.string().trim().max(160).optional().nullable().transform((value) => value || null),
  quantity: z.number().int().positive().max(1000).default(1),
  fulfillmentPreference: z.enum(["pickup", "ship_to_customer", "hold_for_customer"]).default("pickup"),
  estimatedDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  notes: z.string().trim().max(2000).optional().nullable().transform((value) => value || null),
  idempotencyKey: z.string().min(8),
});

export const itemMutationSchema = z.object({
  squareItemId: z.string().optional(),
  squareVersion: z.number().int().optional(),
  squareVariationId: z.string().optional(),
  squareVariationVersion: z.number().int().optional(),
  title: z.string().trim().min(1),
  subtitle: z.string().trim().optional(),
  authors: z.array(z.string().trim()).default([]),
  description: z.string().trim().optional(),
  isbn10: z.string().optional(),
  isbn13: z.string().optional(),
  sku: z.string().trim().optional(),
  gtin: z.string().trim().optional(),
  publisher: z.string().trim().optional(),
  publicationDate: z.string().trim().optional(),
  format: z.string().trim().optional(),
  pageCount: z.number().int().positive().optional(),
  language: z.string().trim().optional(),
  categoryIds: z.array(z.string()).default([]),
  tags: z.array(z.string()).default([]),
  priceMoney: moneySchema,
  active: z.boolean().default(true),
  trackInventory: z.boolean().default(true),
  lowStockThreshold: z.number().int().min(0).default(3),
  locationAvailability: z.array(z.string()).default([]),
  imageUrl: z.string().url().optional(),
  importImage: z.boolean().default(false),
});

export const inventoryAdjustmentSchema = z.object({
  variationId: z.string().min(1),
  locationId: z.string().min(1),
  quantityDelta: z.number().int(),
  reason: z.string().trim().min(1).default("RECEIVE_STOCK"),
  idempotencyKey: z.string().min(8),
});

export const receivingBatchSchema = z.object({
  locationId: z.string().min(1),
  idempotencyKey: z.string().min(8),
  rows: z.array(z.object({
    isbn: z.string().optional(),
    title: z.string().min(1),
    quantity: z.number().int().positive(),
    squareItemId: z.string().optional(),
    squareVariationId: z.string().optional(),
    proposedItem: itemMutationSchema.optional(),
  })).min(1),
});

export const settingsSchema = z.object({
  defaultReceivingLocation: z.string().optional(),
  visibleInventoryLocations: z.array(z.string()).default([]),
  newItemLocationPolicy: z.enum(["all_active", "selected", "receiving_location"]).default("receiving_location"),
  newItemSelectedLocations: z.array(z.string()).default([]),
  defaultCategoryIds: z.array(z.string()).default([]),
  defaultLowStockThreshold: z.number().int().min(0).default(3),
  defaultCurrency: z.string().length(3).default("USD"),
  defaultImportPrices: z.object({
    hardcover: moneySchema.default({ amount: 1300, currency: "USD" }),
    softcover: moneySchema.default({ amount: 700, currency: "USD" }),
    mass_market_paperback: moneySchema.default({ amount: 500, currency: "USD" }),
    new: moneySchema.default({ amount: 2000, currency: "USD" }),
    dvd: moneySchema.default({ amount: 500, currency: "USD" }),
    blu_ray: moneySchema.default({ amount: 700, currency: "USD" }),
    vhs: moneySchema.default({ amount: 300, currency: "USD" }),
    cd: moneySchema.default({ amount: 500, currency: "USD" }),
    vinyl: moneySchema.default({ amount: 1500, currency: "USD" }),
    cassette: moneySchema.default({ amount: 400, currency: "USD" }),
    video_game: moneySchema.default({ amount: 1500, currency: "USD" }),
    merchandise: moneySchema.default({ amount: 500, currency: "USD" }),
    other: moneySchema.default({ amount: 500, currency: "USD" }),
  }).default({
    hardcover: { amount: 1300, currency: "USD" },
    softcover: { amount: 700, currency: "USD" },
    mass_market_paperback: { amount: 500, currency: "USD" },
    new: { amount: 2000, currency: "USD" },
    dvd: { amount: 500, currency: "USD" },
    blu_ray: { amount: 700, currency: "USD" },
    vhs: { amount: 300, currency: "USD" },
    cd: { amount: 500, currency: "USD" },
    vinyl: { amount: 1500, currency: "USD" },
    cassette: { amount: 400, currency: "USD" },
    video_game: { amount: 1500, currency: "USD" },
    merchandise: { amount: 500, currency: "USD" },
    other: { amount: 500, currency: "USD" },
  }),
  customerRequestMinimumPrices: z.object({
    hardcover: moneySchema.default({ amount: 1300, currency: "USD" }),
    softcover: moneySchema.default({ amount: 700, currency: "USD" }),
    new: moneySchema.default({ amount: 2000, currency: "USD" }),
  }).default({
    hardcover: { amount: 1300, currency: "USD" },
    softcover: { amount: 700, currency: "USD" },
    new: { amount: 2000, currency: "USD" },
  }),
  metadataProviderPriority: z.array(z.enum(["google_books", "open_library"])).default(["google_books", "open_library"]),
  autoImportImages: z.boolean().default(false),
  storefrontVisibleLocations: z.array(z.string()).default([]),
  storefrontCategoryVisibility: z.record(z.boolean()).default({}),
  hideOutOfStockStorefront: z.boolean().default(true),
  defaultTradeInPercentage: z.number().int().min(0).max(100).default(50),
  conditionTradeInAdjustments: z.record(z.number().int().min(0).max(200)).default({ new: 100, like_new: 90, very_good: 80, good: 70, acceptable: 50, poor: 0 }),
  reservationDurationMinutes: z.number().int().min(1).max(120).default(15),
  pickupEnabled: z.boolean().default(true),
  shippingEnabled: z.boolean().default(true),
  storefrontDefaultOnlineVisibility: z.boolean().default(true),
  storefrontDefaultPickupEnabled: z.boolean().default(true),
  storefrontDefaultShippingEnabled: z.boolean().default(true),
  flatRateShippingAmount: moneySchema.default({ amount: 0, currency: "USD" }),
  freeShippingThresholdAmount: moneySchema.optional(),
  posDefaultRegisterId: z.string().default("main-register"),
  supplierSearchProviders: z.array(supplierSearchProviderSchema).min(1).max(25).default(defaultSupplierSearchProviders.map((provider) => ({ ...provider, capabilities: [...provider.capabilities], credentialRequirements: [...provider.credentialRequirements] }))),
  labelBackgroundColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default("#FFFFFF"),
  labelTextColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default("#111111"),
});

const nullableTrimmed = (max: number) => z.string().trim().max(max).optional().nullable().transform((value) => value || null);
const safePublicUrl = z.string().trim().max(500).url()
  .refine((value) => {
    const protocol = new URL(value).protocol;
    return protocol === "https:" || protocol === "http:";
  }, "URL must use http or https");
const nullablePublicUrl = safePublicUrl.optional().nullable().transform((value) => value || null);
const safeLogoUrl = z.string().trim().max(500)
  .refine((value) => {
    if (/^\/api\/storefront\/v1\/store-logo\/[A-Za-z0-9_-]+$/.test(value)) return true;
    try {
      const protocol = new URL(value).protocol;
      return protocol === "https:" || protocol === "http:";
    } catch {
      return false;
    }
  }, "Logo URL must use http, https, or the store logo upload route");
const nullableLogoUrl = safeLogoUrl.optional().nullable().transform((value) => value || null);
const nullableEmail = z.string().trim().max(254).email().optional().nullable().transform((value) => value || null);
const timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use 24-hour HH:mm time");

export const socialPlatformSchema = z.enum(["facebook", "instagram", "tiktok", "xTwitter", "youtube", "bluesky", "threads"]);
export const businessHoursSchema = z.array(z.object({
  dayOfWeek: z.enum(["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]),
  closed: z.boolean().default(false),
  intervals: z.array(z.object({
    opens: timeOfDaySchema,
    closes: timeOfDaySchema,
  }).refine((value) => value.opens < value.closes, "Opening time must be before closing time")).max(3).default([]),
}).refine((value) => value.closed || value.intervals.length > 0, "Open days need at least one interval")).max(7).default([]);

export const storeProfileSchema = z.object({
  storeDisplayName: z.string().trim().min(1).max(120),
  productName: z.literal("PaperbackOS").default("PaperbackOS"),
  storeTagline: nullableTrimmed(180),
  storeDescription: nullableTrimmed(1000),
  logoUrl: nullableLogoUrl,
  publicWebsiteUrl: nullablePublicUrl,
  publicEmail: nullableEmail,
  publicPhone: nullableTrimmed(40),
  addressLine1: nullableTrimmed(160),
  addressLine2: nullableTrimmed(160),
  city: nullableTrimmed(100),
  region: nullableTrimmed(100),
  postalCode: nullableTrimmed(40),
  country: nullableTrimmed(80),
  socialLinks: z.object({
    facebook: safePublicUrl.optional(),
    instagram: safePublicUrl.optional(),
    tiktok: safePublicUrl.optional(),
    xTwitter: safePublicUrl.optional(),
    youtube: safePublicUrl.optional(),
    bluesky: safePublicUrl.optional(),
    threads: safePublicUrl.optional(),
  }).default({}),
  businessHours: businessHoursSchema,
  timezone: z.string().trim().max(100).optional().nullable().transform((value) => value || null)
    .refine((value) => !value || isSupportedTimeZone(value), "Unsupported timezone"),
  currencyCode: z.string().trim().length(3).transform((value) => value.toUpperCase()).optional().nullable().transform((value) => value || null),
  receiptFooterText: nullableTrimmed(600),
  storefrontTitle: nullableTrimmed(160),
  storefrontDescription: nullableTrimmed(320),
});

export const storeProfileUpdateSchema = storeProfileSchema.omit({ productName: true });

export const storeLogoUploadSchema = z.object({
  filename: z.string().trim().max(180).optional(),
  contentType: z.enum(["image/png", "image/jpeg", "image/webp", "image/svg+xml"]),
  dataBase64: z.string().min(1).max(350000).regex(/^[A-Za-z0-9+/]+={0,2}$/, "Logo payload must be base64"),
});

export const checkoutSchema = z.object({
  idempotencyKey: z.string().min(8),
  successUrl: z.string().url().optional(),
  cancelUrl: z.string().url().optional(),
  locationId: z.string().optional(),
  items: z.array(z.object({
    variationId: z.string().min(1),
    quantity: z.number().int().positive().max(99),
  })).min(1).max(50),
});

export const posCartLineSchema = z.object({
  variationId: z.string().min(1),
  quantity: z.number().int().positive().max(99),
  discountAmount: z.number().int().min(0).default(0),
  discountReason: z.string().trim().max(200).optional(),
});

export const posQuoteSchema = z.object({
  locationId: z.string().optional(),
  customerId: z.string().optional(),
  storeCreditAmount: z.number().int().min(0).default(0),
  lines: z.array(posCartLineSchema).min(1).max(100),
});

export const posTerminalCheckoutSchema = posQuoteSchema.extend({
  registerId: z.string().min(1),
  terminalDeviceId: z.string().min(1).optional(),
  idempotencyKey: z.string().min(8),
  note: z.string().trim().max(500).optional(),
});

export const posRegisterOpenSchema = z.object({
  registerId: z.string().min(1),
  locationId: z.string().min(1).default("loc-main"),
  openingCashAmount: z.number().int().min(0).default(0),
  idempotencyKey: z.string().min(8),
});

export const posRegisterCloseSchema = z.object({
  countedCashAmount: z.number().int().min(0),
  closingNotes: z.string().trim().max(500).optional(),
  idempotencyKey: z.string().min(8),
});

export const posCashMovementSchema = z.object({
  movementType: z.enum(["paid_in", "paid_out", "correction"]),
  amount: z.number().int().positive(),
  reason: z.string().trim().min(1).max(300),
  idempotencyKey: z.string().min(8),
});

export const posSaleCreateSchema = z.object({
  registerSessionId: z.string().min(1),
  customerId: z.string().min(1).optional(),
  notes: z.string().trim().max(500).optional(),
  idempotencyKey: z.string().min(8),
});

export const posSaleResolveSchema = z.object({
  saleId: z.string().min(1),
  input: z.string().trim().min(1).max(200),
});

export const posScannerSessionCreateSchema = z.object({
  expiresInMinutes: z.number().int().min(5).max(720).default(240),
});

export const posScannerScanCreateSchema = z.object({
  input: z.string().trim().min(1).max(200),
  idempotencyKey: z.string().trim().min(8).max(120).optional(),
});

export const posScannerScanAckSchema = z.object({
  status: z.enum(["processed", "needs_review", "failed"]),
  message: z.string().trim().max(500).optional(),
});

export const posSaleSearchSchema = z.object({
  q: z.string().trim().min(1).max(120),
  locationId: z.string().min(1).optional(),
  limit: z.coerce.number().int().positive().max(50).default(20),
});

export const posSaleAddLineSchema = z.object({
  saleId: z.string().min(1),
  variationId: z.string().min(1).optional(),
  copyId: z.string().min(1).optional(),
  quantity: z.number().int().positive().max(99).default(1),
  idempotencyKey: z.string().min(8),
}).refine((value) => value.variationId || value.copyId, "Variation or exact copy is required");

export const posSaleManualLineSchema = z.object({
  saleId: z.string().min(1),
  description: z.string().trim().min(1).max(200),
  unitPriceAmount: z.number().int().min(0).max(999999),
  quantity: z.number().int().positive().max(99).default(1),
  giftCardPurchase: z.boolean().default(false),
  idempotencyKey: z.string().min(8),
});

export const posSaleUpdateLineSchema = z.object({
  quantity: z.number().int().positive().max(99),
  idempotencyKey: z.string().min(8),
});

export const posSaleDiscountSchema = z.object({
  lineId: z.string().min(1).optional(),
  discountDefinitionId: z.string().min(1).optional(),
  discountType: z.enum(["fixed", "percentage"]),
  valueAmount: z.number().int().positive(),
  reason: z.string().trim().min(1).max(300),
  idempotencyKey: z.string().min(8),
});

export const posDiscountDefinitionSaveSchema = z.object({
  id: z.string().trim().min(1).optional(),
  name: z.string().trim().min(1).max(120),
  discountType: z.enum(["fixed", "percentage"]),
  valueAmount: z.number().int().min(0).max(10000000),
  scope: z.enum(["line", "order"]),
  applicationMode: z.enum(["manual", "automatic", "both"]).default("automatic"),
  active: z.boolean().default(true),
  requiresManager: z.boolean().default(false),
  requiresReason: z.boolean().default(false),
  locationId: z.string().trim().min(1).optional().nullable(),
  startsAt: z.string().trim().optional().nullable(),
  endsAt: z.string().trim().optional().nullable(),
  daysOfWeek: z.array(z.enum(["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"])).default([]),
  startTime: z.string().trim().regex(/^\d{2}:\d{2}$/).optional().nullable(),
  endTime: z.string().trim().regex(/^\d{2}:\d{2}$/).optional().nullable(),
  minimumSubtotalAmount: z.number().int().min(0).max(10000000).default(0),
  minimumItemCount: z.number().int().min(0).max(100000).default(0),
  minimumMatchingItemCount: z.number().int().min(0).max(100000).default(0),
  productType: z.enum(["book", "movie", "music", "game", "merchandise", "other"]).optional().nullable(),
  formatMatch: z.string().trim().max(80).optional().nullable(),
  conditionMatch: z.string().trim().max(80).optional().nullable(),
  skuMatch: z.string().trim().max(120).optional().nullable(),
  titleMatch: z.string().trim().max(160).optional().nullable(),
  priority: z.number().int().min(0).max(9999).default(100),
  stackable: z.boolean().default(true),
});

export const posSaleCashCheckoutSchema = z.object({
  saleId: z.string().min(1),
  cashTenderedAmount: z.number().int().min(0),
  idempotencyKey: z.string().min(8),
});

export const posSaleSquareCheckoutSchema = z.object({
  saleId: z.string().min(1),
  terminalDeviceId: z.string().min(1).optional(),
  note: z.string().trim().max(500).optional(),
  idempotencyKey: z.string().min(8),
});

export const posSaleSquareAppCheckoutSchema = z.object({
  saleId: z.string().min(1),
  note: z.string().trim().max(500).optional(),
  idempotencyKey: z.string().min(8),
});

export const posSalePaymentStatusSchema = z.object({
  status: z.enum(["completed", "failed", "cancelled", "unknown", "requires_reconciliation"]),
  providerPaymentId: z.string().trim().max(120).optional(),
  tenderType: z.enum(["card", "gift_card"]).optional(),
  failureCode: z.string().trim().max(80).optional(),
  failureMessage: z.string().trim().max(300).optional(),
  idempotencyKey: z.string().min(8),
});

export const posStoreCreditHoldSchema = z.object({
  requestedAmount: z.number().int().positive().optional(),
  idempotencyKey: z.string().min(8),
});

export const posStoreCreditCaptureSchema = z.object({
  expectedStatus: z.literal("active"),
  expectedVersion: z.number().int().positive(),
  idempotencyKey: z.string().min(8),
});

export const posStoreCreditReleaseSchema = z.object({
  expectedStatus: z.literal("active"),
  expectedVersion: z.number().int().positive(),
  idempotencyKey: z.string().min(8),
});

export const tradeInCreditRedemptionRuleSchema = z.object({
  enabled: z.boolean().default(true),
  maxPercent: z.number().int().min(0).max(100).default(100),
});

export const tradeInCreditRedemptionRulesSchema = z.object({
  default: tradeInCreditRedemptionRuleSchema.default({ enabled: true, maxPercent: 100 }),
  categories: z.record(tradeInCreditRedemptionRuleSchema).default({}),
});

export const tradeInPolicySettingsSchema = z.object({
  enabled: z.boolean().default(true),
  creditMode: z.enum(["dollar_amount", "one_for_one"]).default("dollar_amount"),
  defaultCreditPercentage: z.number().int().min(0).max(100).default(50),
  oneForOneCreditAmount: z.number().int().min(0).default(1),
  minItemPriceAmount: z.number().int().min(0).default(0),
  maxItemPriceAmount: z.number().int().min(0).default(1_000_000),
  currency: z.string().length(3).default("USD"),
  quoteExpirationMinutes: z.number().int().min(0).default(1440),
  requireCustomer: z.boolean().default(false),
  requireInspection: z.boolean().default(true),
  managerApprovalThresholdAmount: z.number().int().min(0).default(5000),
  conditionAdjustments: z.record(z.number().int().min(0).max(200)).default({ new: 100, like_new: 90, very_good: 80, good: 70, acceptable: 50, poor: 0 }),
  allowedReasons: z.array(z.string().trim().min(1).max(80)).min(1).max(50).default(["standard_trade_in", "condition_reject", "policy_reject", "customer_cancelled", "other"]),
  redemptionRules: tradeInCreditRedemptionRulesSchema.default({ default: { enabled: true, maxPercent: 100 }, categories: {} }),
  modeChangeConversionAmount: z.number().int().positive().optional(),
  policyText: z.string().trim().max(2000).optional().nullable().transform((value) => value || null),
}).refine((value) => value.maxItemPriceAmount >= value.minItemPriceAmount, "Max item price must be greater than or equal to min item price");

export const posStoreCreditReversalSchema = z.object({
  expectedStatus: z.literal("completed"),
  expectedHoldVersion: z.number().int().positive(),
  sellableReturnAccepted: z.literal(true),
  reasonCode: z.enum(["customer_return", "staff_error", "duplicate_sale", "damaged_after_sale", "other"]),
  note: z.string().trim().max(500).optional(),
  lines: z.array(z.object({
    originalSaleLineId: z.string().min(1),
    copyId: z.string().min(1).optional(),
    sellableQuantity: z.number().int().min(0).default(0),
    damagedQuantity: z.number().int().min(0).default(0),
    disposition: z.enum(["restock_sellable", "damaged", "mixed_sellable_damaged"]).optional(),
    damagedNote: z.string().trim().max(500).optional(),
  }).strict()).optional(),
  idempotencyKey: z.string().min(8),
}).strict().superRefine((value, ctx) => {
  for (const line of value.lines ?? []) {
    const total = line.sellableQuantity + line.damagedQuantity;
    if (total <= 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["lines"], message: "Each reversal line must return at least one unit" });
    }
    if (line.disposition === "restock_sellable" && line.damagedQuantity !== 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["lines"], message: "Sellable lines cannot include damaged quantity" });
    }
    if (line.disposition === "damaged" && line.sellableQuantity !== 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["lines"], message: "Damaged lines cannot include sellable quantity" });
    }
    if (line.disposition === "mixed_sellable_damaged" && (line.sellableQuantity <= 0 || line.damagedQuantity <= 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["lines"], message: "Mixed disposition requires sellable and damaged quantities" });
    }
    if (line.damagedQuantity > 0 && !line.damagedNote?.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["lines"], message: "Damaged returned units require a staff note" });
    }
  }
});

export const customerSearchSchema = z.object({
  q: z.string().trim().min(1).max(100),
});

export const customerCreateSchema = z.object({
  givenName: z.string().trim().max(80).optional(),
  familyName: z.string().trim().max(80).optional(),
  emailAddress: z.string().email().optional(),
  phoneNumber: z.string().trim().max(30).optional(),
  birthMonthDay: monthDaySchema,
  loyaltyAnniversaryMonthDay: monthDaySchema,
}).refine((value) => value.givenName || value.familyName || value.emailAddress || value.phoneNumber, "Provide customer details");

export const customerUpdateSchema = customerCreateSchema;

export const customerRequestStatusSchema = z.enum(["requested", "sourcing", "ordered", "received", "purchased", "cancelled"]);

export const customerRequestCreateSchema = z.object({
  customerId: z.string().min(1).optional(),
  squareCustomerId: z.string().min(1).optional(),
  title: z.string().trim().min(1).max(200),
  author: z.string().trim().max(160).optional(),
  isbn: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid ISBN").optional(),
  notes: z.string().trim().max(1000).optional(),
  requestFormat: z.enum(["hardcover", "softcover", "new"]).default("softcover"),
  metadata: bookMetadataSchema.optional(),
}).refine((value) => value.customerId || value.squareCustomerId, "Customer is required");

export const customerRequestUpdateSchema = z.object({
  status: customerRequestStatusSchema.optional(),
  title: z.string().trim().min(1).max(200).optional(),
  author: z.string().trim().max(160).optional(),
  isbn: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid ISBN").optional(),
  notes: z.string().trim().max(1000).optional(),
  requestFormat: z.enum(["hardcover", "softcover", "new"]).optional(),
  metadata: bookMetadataSchema.optional(),
  supplierName: z.string().trim().max(120).optional(),
  supplierUrl: z.string().url().optional(),
  foundPriceMoney: moneySchema.optional(),
  shippingMoney: moneySchema.optional(),
  taxMoney: moneySchema.optional(),
  landedCostMoney: moneySchema.optional(),
  customerPriceMoney: moneySchema.optional(),
});

export const tradeInQuoteSchema = z.object({
  customerId: z.string().optional(),
  idempotencyKey: z.string().min(8).optional(),
  items: z.array(z.object({
    isbn: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid ISBN").optional(),
    title: z.string().trim().min(1),
    authors: z.array(z.string().trim()).default([]),
    condition: z.enum(["new", "like_new", "very_good", "good", "acceptable", "poor"]),
    intendedPriceMoney: moneySchema,
    notes: z.string().trim().max(500).optional(),
  })).min(1).max(100),
});

export const tradeInCompleteSchema = tradeInQuoteSchema.extend({
  customerId: z.string().min(1),
  locationId: z.string().min(1),
  idempotencyKey: z.string().min(8),
});

export const tradeInLifecycleTransitionSchema = z.object({
  expectedStatus: z.enum(["draft", "quoted", "awaiting_inspection", "awaiting_approval", "approved", "completed", "cancelled", "rejected", "exception"]),
  toStatus: z.enum(["awaiting_inspection", "awaiting_approval", "approved", "cancelled", "rejected"]),
  expectedVersion: z.number().int().positive(),
  reasonCode: z.string().trim().max(80).optional(),
  reasonText: z.string().trim().max(500).optional(),
  idempotencyKey: z.string().min(8),
});

export const tradeInInventoryAcquisitionSchema = z.object({
  expectedStatus: z.literal("approved"),
  expectedVersion: z.number().int().positive(),
  locationId: z.string().trim().min(1).default("loc-main"),
  idempotencyKey: z.string().min(8),
  items: z.array(z.object({
    draftItemId: z.string().min(1),
    variationId: z.string().min(1),
    shelfCode: z.string().trim().max(80).optional(),
  })).min(1).max(100),
});

export const tradeInCreditIssuanceSchema = z.object({
  expectedStatus: z.literal("approved"),
  expectedVersion: z.number().int().positive(),
  idempotencyKey: z.string().min(8),
});

export const tradeInCreditReversalSchema = z.object({
  expectedStatus: z.literal("completed"),
  expectedVersion: z.number().int().positive(),
  reasonCode: z.enum(["staff_error", "customer_cancelled", "condition_error", "policy_error", "other"]),
  reasonText: z.string().trim().max(500).optional(),
  idempotencyKey: z.string().min(8),
});

export const storefrontCartCreateSchema = z.object({
  idempotencyKey: z.string().min(8).optional(),
  locationId: z.string().trim().min(1).optional(),
});

export const storefrontCartUpdateSchema = z.object({
  cartId: z.string().min(16),
  token: z.string().min(32),
  lines: z.array(z.object({
    variationId: z.string().min(1),
    copyId: z.string().min(1).optional(),
    quantity: z.number().int().min(0).max(20),
  })).max(50),
});

export const storefrontGiftCardCartUpdateSchema = z.object({
  cartId: z.string().min(16),
  token: z.string().min(32),
  lines: z.array(z.object({
    id: z.string().trim().min(1).optional(),
    amount: z.number().int().min(100).max(200000),
    quantity: z.number().int().min(0).max(20).default(1),
    recipientName: z.string().trim().max(120).optional(),
    recipientEmail: z.string().email().optional().or(z.literal("")).transform((value) => value || undefined),
    senderName: z.string().trim().max(120).optional(),
    message: z.string().trim().max(300).optional(),
  })).max(20),
});

export const storefrontShippingAddressSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  line1: z.string().trim().min(1).max(160),
  line2: z.string().trim().max(160).optional(),
  city: z.string().trim().min(1).max(100),
  region: z.string().trim().min(2).max(32),
  postalCode: z.string().trim().min(3).max(20),
  country: z.string().trim().length(2).transform((value) => value.toUpperCase()),
});

export const storefrontCartCheckoutSchema = z.object({
  cartId: z.string().min(16),
  token: z.string().min(32),
  idempotencyKey: z.string().min(8),
  loyaltyRewardDefinitionId: z.string().trim().min(1).optional(),
  customer: z.object({
    name: z.string().trim().min(1).max(120),
    email: z.string().email(),
    phone: z.string().trim().max(30).optional(),
  }),
  fulfillment: z.object({
    method: z.enum(["pickup", "shipping"]).default("pickup"),
    locationId: z.string().optional(),
    shippingAddress: storefrontShippingAddressSchema.optional(),
    shippingMethodId: z.enum(["flat_rate"]).default("flat_rate").optional(),
  }).superRefine((value, ctx) => {
    if (value.method === "shipping" && !value.shippingAddress) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["shippingAddress"], message: "Shipping address is required for shipped orders" });
    }
  }),
  successUrl: z.string().url().optional(),
  cancelUrl: z.string().url().optional(),
});

export const storefrontStoreCreditConversionSchema = z.object({
  rewardDefinitionId: z.string().trim().min(1),
  idempotencyKey: z.string().min(8),
});

export const storefrontReservationCancelSchema = z.object({
  token: z.string().min(32),
  idempotencyKey: z.string().min(8),
  reason: z.string().trim().max(500).optional(),
});

export const storefrontPaymentStatusSchema = z.object({
  cartId: z.string().min(16),
  token: z.string().min(32).optional(),
  status: z.enum(["completed", "failed", "cancelled", "unknown", "requires_reconciliation"]),
  providerPaymentId: z.string().trim().max(120).optional(),
  providerOrderId: z.string().trim().max(120).optional(),
  failureCode: z.string().trim().max(80).optional(),
  failureMessage: z.string().trim().max(300).optional(),
  idempotencyKey: z.string().min(8),
});

export const storefrontFulfillmentUpdateSchema = z.object({
  status: z.enum(["awaiting_fulfillment", "picking", "packed", "shipped", "ready_for_pickup", "completed", "exception", "cancelled"]),
  carrierName: z.string().trim().max(80).optional(),
  trackingNumber: z.string().trim().max(120).optional(),
  internalNotes: z.string().trim().max(1000).optional(),
  customerNotes: z.string().trim().max(1000).optional(),
  idempotencyKey: z.string().min(8),
});

export const returnDispositionSchema = z.enum([
  "restock_sellable",
  "quarantine",
  "damaged",
  "incomplete",
  "missing_components",
  "discard",
  "retain_for_review",
  "no_physical_return",
  "other",
]);

export const returnEligibilitySchema = z.object({
  saleId: z.string().min(1),
  saleSource: z.enum(["pos", "storefront"]).default("pos"),
});

export const returnLineRequestSchema = z.object({
  originalSaleLineId: z.string().min(1),
  quantity: z.number().int().positive(),
  disposition: returnDispositionSchema.default("retain_for_review"),
  reason: z.string().trim().max(120).optional(),
});

export const returnCreateSchema = z.object({
  saleId: z.string().min(1),
  saleSource: z.enum(["pos", "storefront"]).default("pos"),
  reason: z.string().trim().min(1).max(120),
  customerNote: z.string().trim().max(500).optional(),
  internalNote: z.string().trim().max(1000).optional(),
  lines: z.array(returnLineRequestSchema).min(1).max(50),
  idempotencyKey: z.string().min(8),
});

export const returnDispositionUpdateSchema = z.object({
  lineId: z.string().min(1),
  disposition: returnDispositionSchema,
  quantity: z.number().int().positive().optional(),
  reason: z.string().trim().min(1).max(200),
  notes: z.string().trim().max(1000).optional(),
  idempotencyKey: z.string().min(8),
});

export const returnApprovalSchema = z.object({
  approved: z.boolean(),
  reason: z.string().trim().min(1).max(300),
  idempotencyKey: z.string().min(8),
});

export const returnRefundSchema = z.object({
  refundMethod: z.enum(["cash", "square"]),
  amount: z.number().int().positive().optional(),
  registerSessionId: z.string().min(1).optional(),
  providerPaymentId: z.string().trim().max(120).optional(),
  reason: z.string().trim().max(200).optional(),
  idempotencyKey: z.string().min(8),
});

export const refundReconcileSchema = z.object({
  refundAttemptId: z.string().min(1),
  status: z.enum(["succeeded", "failed", "cancelled", "unknown", "reconciliation_required"]),
  providerRefundId: z.string().trim().max(120).optional(),
  failureCategory: z.string().trim().max(80).optional(),
  failureReason: z.string().trim().max(300).optional(),
  idempotencyKey: z.string().min(8),
});

export const reconciliationCaseStatusSchema = z.object({
  status: z.enum(["open", "investigating", "awaiting_provider", "awaiting_local_retry", "resolved", "dismissed", "escalated"]),
  reason: z.string().trim().min(1).max(500),
  idempotencyKey: z.string().min(8),
});

export const providerLookupSchema = z.object({
  idempotencyKey: z.string().min(8),
});

export const reservationExpirationSchema = z.object({
  limit: z.number().int().min(1).max(100).optional(),
  preview: z.boolean().default(false),
  idempotencyKey: z.string().min(8),
});

export const exchangeLinkSchema = z.object({
  replacementSaleId: z.string().min(1),
  replacementSaleSource: z.enum(["pos"]).default("pos"),
  idempotencyKey: z.string().min(8),
});

export const returnPolicySettingsSchema = z.object({
  returnsEnabled: z.boolean(),
  defaultReturnWindowDays: z.number().int().min(0).max(3650),
  receiptRequired: z.boolean(),
  managerApprovalThresholdAmount: z.number().int().min(0),
  managerRequiredForNoReceipt: z.boolean(),
  managerRequiredForMethodOverride: z.boolean(),
  defaultDisposition: returnDispositionSchema,
  cashRefundsRequireOpenRegister: z.boolean(),
  allowedReasons: z.array(z.string().trim().min(1).max(80)).min(1).max(50),
  policyText: z.string().trim().max(2000).optional(),
});

export const visibilitySchema = z.object({
  variationId: z.string().min(1),
  squareItemId: z.string().min(1),
  visibleOnline: z.boolean(),
  pickupEnabled: z.boolean().default(true),
  shippingEnabled: z.boolean().default(false),
  featured: z.boolean().default(false),
});

export const visibilityBatchSchema = z.object({
  variationIds: z.array(z.string().trim().min(1)).min(1).max(250),
  visibleOnline: z.boolean().optional(),
  pickupEnabled: z.boolean().optional(),
  shippingEnabled: z.boolean().optional(),
  featured: z.boolean().optional(),
}).refine((value) =>
  value.visibleOnline !== undefined
  || value.pickupEnabled !== undefined
  || value.shippingEnabled !== undefined
  || value.featured !== undefined,
{ message: "Choose at least one publishing field to update." });

export const categoryVisibilitySchema = z.object({
  categories: z.array(z.object({
    categoryId: z.string().min(1).optional(),
    categoryIds: z.array(z.string().min(1)).optional(),
    name: z.string().min(1),
    visible: z.boolean(),
  })).max(500),
});

export const orderStatusUpdateSchema = z.object({
  status: z.enum(["checkout_started", "paid", "processing", "ready_for_pickup", "shipped", "completed", "cancelled", "refunded"]),
  trackingNumber: z.string().trim().max(120).optional(),
  notes: z.string().trim().max(500).optional(),
});

export const trackingModeSchema = z.enum(["copy", "quantity"]);
export const inventoryCopyStatusSchema = z.enum(["available", "reserved", "sold", "damaged", "lost", "returned", "archived"]);
export const inventoryMovementTypeSchema = z.enum([
  "initial_import",
  "receiving",
  "trade_in_acquisition",
  "purchase_order_receiving",
  "sale",
  "return",
  "exchange",
  "reservation",
  "reservation_release",
  "damage",
  "loss",
  "theft_or_shrinkage",
  "manual_adjustment",
  "location_transfer",
  "import_reconciliation",
  "import_reversal",
  "consignment_receipt",
  "consignment_return",
  "cancellation_correction",
]);

export const specialEditionAttributesSchema = z.object({
  firstEdition: z.boolean().optional(),
  firstPrinting: z.boolean().optional(),
  signed: z.boolean().optional(),
  inscribed: z.boolean().optional(),
  numberedEdition: z.boolean().optional(),
  limitedEdition: z.boolean().optional(),
  advancedReaderCopy: z.boolean().optional(),
  bookClubEdition: z.boolean().optional(),
  exLibrary: z.boolean().optional(),
  illustratedEdition: z.boolean().optional(),
  annotatedEdition: z.boolean().optional(),
  collectorsEdition: z.boolean().optional(),
  dustJacketPresent: z.boolean().optional(),
  dustJacketCondition: z.string().trim().max(120).optional(),
  printingNumber: z.string().trim().max(80).optional(),
  provenance: z.string().trim().max(1000).optional(),
  authenticationNotes: z.string().trim().max(1000).optional(),
}).strict();

export const localWorkCreateSchema = z.object({
  title: z.string().trim().min(1).max(300),
  subtitle: z.string().trim().max(300).optional(),
  description: z.string().trim().max(10000).optional(),
  language: z.string().trim().max(20).optional(),
  contributors: z.array(z.object({
    displayName: z.string().trim().min(1).max(200),
    sortName: z.string().trim().max(200).optional(),
    role: z.string().trim().min(1).max(80).default("author"),
  })).default([]),
});

export const localEditionCreateSchema = z.object({
  workId: z.string().min(1).optional(),
  work: localWorkCreateSchema.optional(),
  publisher: z.string().trim().max(200).optional(),
  imprint: z.string().trim().max(200).optional(),
  publicationDate: z.string().trim().max(40).optional(),
  language: z.string().trim().max(20).optional(),
  pageCount: z.number().int().positive().optional(),
  dimensions: z.string().trim().max(120).optional(),
  weight: z.string().trim().max(120).optional(),
  descriptionOverride: z.string().trim().max(10000).optional(),
  coverImageOverride: z.string().url().optional(),
  editionNotes: z.string().trim().max(1000).optional(),
  metadataProvenance: z.record(z.unknown()).optional(),
  metadataConfidence: z.string().trim().max(80).optional(),
  identifiers: z.array(z.object({
    identifierType: z.enum(["isbn10", "isbn13", "gtin"]).or(z.string().trim().min(1).max(40)),
    value: z.string().trim().min(1).max(80),
    displayValue: z.string().trim().max(100).optional(),
    source: z.string().trim().max(80).optional(),
    primary: z.boolean().default(false),
  })).default([]),
}).refine((value) => value.workId || value.work, "Provide workId or work");

export const localVariationCreateSchema = z.object({
  editionId: z.string().min(1),
  formatCode: z.string().trim().min(1).max(80).default("other"),
  productType: productTypeSchema.default("book"),
  internalSku: z.string().trim().max(100).optional(),
  defaultCostAmount: z.number().int().min(0).default(0),
  listPriceAmount: z.number().int().min(0).optional(),
  defaultSellingPriceAmount: z.number().int().min(0).default(0),
  currency: z.string().length(3).default("USD"),
  taxCategoryId: z.string().trim().max(80).optional(),
  barcodeBehavior: z.string().trim().max(80).default("store_barcode"),
  trackingMode: trackingModeSchema.default("quantity"),
  specialAttributes: specialEditionAttributesSchema.default({}),
  onlineVisibility: z.boolean().optional(),
});

export const localCatalogItemUpdateSchema = z.object({
  variationId: z.string().min(1),
  title: z.string().trim().min(1),
  authors: z.array(z.string().trim().min(1)).default([]),
  description: z.string().trim().max(10000).optional(),
  isbn10: z.string().trim().max(20).optional(),
  isbn13: z.string().trim().max(20).optional(),
  sku: z.string().trim().max(100).optional(),
  publisher: z.string().trim().max(200).optional(),
  publicationDate: z.string().trim().max(40).optional(),
  formatCode: z.string().trim().max(80).optional(),
  productType: productTypeSchema.optional(),
  pageCount: z.number().int().positive().optional(),
  language: z.string().trim().max(20).optional(),
  coverImageUrl: z.string().url().optional(),
  priceMoney: moneySchema,
});

export const catalogMetadataRefreshSchema = z.object({
  mode: z.enum(["replace_mapped", "fill_missing"]).default("replace_mapped"),
});

export const catalogMetadataBulkRefreshSchema = z.object({
  mode: z.literal("fill_missing").default("fill_missing"),
  limit: z.number().int().min(1).max(1000).default(1000),
});

export const localCopyCreateSchema = z.object({
  variationId: z.string().min(1),
  inventoryNumber: z.string().trim().max(80).optional(),
  storeBarcode: z.string().trim().max(120).optional(),
  condition: z.string().trim().min(1).max(80).default("good"),
  conditionNotes: z.string().trim().max(1000).optional(),
  acquisitionCostAmount: z.number().int().min(0).default(0),
  sellingPriceAmount: z.number().int().min(0),
  listPriceAmount: z.number().int().min(0).optional(),
  currency: z.string().length(3).default("USD"),
  acquisitionSource: z.string().trim().max(80).default("receiving"),
  locationId: z.string().min(1).default("loc-main"),
  shelfCode: z.string().trim().max(80).optional(),
  onlineVisibility: z.boolean().default(false),
  idempotencyKey: z.string().min(8).optional(),
});

export const localQuantityReceiveSchema = z.object({
  variationId: z.string().min(1),
  locationId: z.string().min(1).default("loc-main"),
  quantity: z.number().int().positive(),
  costBasisAmount: z.number().int().min(0).default(0),
  reason: z.string().trim().max(200).default("Receiving"),
  sourceEntityType: z.string().trim().max(80).default("manual"),
  sourceEntityId: z.string().trim().max(120).optional(),
  idempotencyKey: z.string().min(8),
});

export const localQuantityAdjustmentSchema = z.object({
  variationId: z.string().min(1),
  locationId: z.string().min(1).default("loc-main"),
  targetQuantity: z.number().int().min(0),
  reason: z.string().trim().min(1).max(200).default("Manual count correction"),
  idempotencyKey: z.string().min(8),
});

export const purchasingBackorderPolicySchema = z.enum(["allow", "cancel", "ship_available"]);
export const purchasingAddressTypeSchema = z.enum(["ship_to", "bill_to", "remit_to", "ordering"]);
export const purchasingPartyIdentifierTypeSchema = z.enum(["san", "pubnet_id", "vendor_account_id", "x12_sender_id", "x12_receiver_id", "gln", "other"]);
export const purchasingReturnabilitySchema = z.enum(["returnable", "non_returnable", "conditional", "unknown"]);
export const purchasingPoTypeSchema = z.enum(["replenishment", "customer_special_order", "preorder", "event_book_fair", "initial_inventory", "direct_to_customer", "manual"]);
export const purchasingPoStatusSchema = z.enum(["draft", "pending_approval", "approved", "submitted", "acknowledged", "partially_filled", "shipped", "partially_received", "received", "completed", "canceled", "closed"]);
export const purchasingPoChangeTypeSchema = z.enum(["cancel_po", "cancel_line", "reduce_quantity", "increase_quantity", "change_requested_ship_date", "change_cancel_if_not_filled_date", "change_backorder_preference", "change_shipping_instructions", "replace_edition", "close_backorder"]);
export const purchasingPoChangeStatusSchema = z.enum(["draft", "pending_approval", "queued", "sent", "supplier_received", "accepted", "partially_accepted", "rejected", "failed", "superseded", "canceled"]);
export const purchasingPoChangeTransmissionSchema = z.enum(["electronic", "email", "phone", "manual_contact"]);

const optionalTrimmed = (max: number) => z.string().trim().max(max).optional().nullable().transform((value) => value || null);
const idField = z.string().trim().min(1).max(160);

export const purchasingVendorSaveSchema = z.object({
  name: z.string().trim().min(1).max(200),
  legalName: optionalTrimmed(240),
  status: z.enum(["active", "inactive"]).default("active"),
  orderingEmail: z.string().trim().email().optional().nullable().transform((value) => value || null),
  orderingPhone: optionalTrimmed(60),
  orderingContactName: optionalTrimmed(160),
  paymentTerms: optionalTrimmed(200),
  defaultCurrency: z.string().trim().length(3).default("USD"),
  defaultShippingMethod: optionalTrimmed(120),
  defaultBackorderPolicy: purchasingBackorderPolicySchema.default("allow"),
  minimumOrderAmount: z.number().int().min(0).optional().nullable().transform((value) => value ?? null),
  freeFreightThresholdAmount: z.number().int().min(0).optional().nullable().transform((value) => value ?? null),
  notes: optionalTrimmed(2000),
  preferred: z.boolean().default(false),
});

export const purchasingVendorPatchSchema = purchasingVendorSaveSchema.partial();

export const purchasingVendorAccountSaveSchema = z.object({
  accountNumber: z.string().trim().min(1).max(120),
  accountName: optionalTrimmed(160),
  status: z.enum(["active", "inactive"]).default("active"),
  orderingEmail: z.string().trim().email().optional().nullable().transform((value) => value || null),
  orderingPhone: optionalTrimmed(60),
  orderingContactName: optionalTrimmed(160),
  paymentTerms: optionalTrimmed(200),
  currency: z.string().trim().length(3).default("USD"),
  shippingMethod: optionalTrimmed(120),
  backorderPolicy: z.enum(["vendor_default", "allow", "cancel", "ship_available"]).default("vendor_default"),
  minimumOrderAmount: z.number().int().min(0).optional().nullable().transform((value) => value ?? null),
  freeFreightThresholdAmount: z.number().int().min(0).optional().nullable().transform((value) => value ?? null),
  notes: optionalTrimmed(2000),
});

export const purchasingVendorAccountPatchSchema = purchasingVendorAccountSaveSchema.partial();

export const purchasingVendorAddressSaveSchema = z.object({
  vendorAccountId: idField.optional().nullable().transform((value) => value || null),
  addressType: purchasingAddressTypeSchema,
  label: z.string().trim().min(1).max(160),
  contactName: optionalTrimmed(160),
  companyName: optionalTrimmed(200),
  line1: optionalTrimmed(240),
  line2: optionalTrimmed(240),
  city: optionalTrimmed(120),
  region: optionalTrimmed(80),
  postalCode: optionalTrimmed(40),
  country: z.string().trim().min(2).max(2).default("US"),
  phone: optionalTrimmed(60),
  email: z.string().trim().email().optional().nullable().transform((value) => value || null),
  defaultForType: z.boolean().default(false),
  active: z.boolean().default(true),
});

export const purchasingPartyIdentifierSaveSchema = z.object({
  vendorAccountId: idField.optional().nullable().transform((value) => value || null),
  addressId: idField.optional().nullable().transform((value) => value || null),
  identifierType: purchasingPartyIdentifierTypeSchema,
  identifierValue: z.string().trim().min(1).max(160),
  qualifier: optionalTrimmed(80),
  metadata: z.record(z.unknown()).default({}),
  active: z.boolean().default(true),
});

const purchasingVendorProductBaseSchema = z.object({
  vendorId: idField,
  vendorAccountId: idField.optional().nullable().transform((value) => value || null),
  editionId: idField.optional().nullable().transform((value) => value || null),
  variationId: idField.optional().nullable().transform((value) => value || null),
  isbn13: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid ISBN-13").optional().nullable().transform((value) => value || null),
  isbn10: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid ISBN-10").optional().nullable().transform((value) => value || null),
  upcEanGtin: optionalTrimmed(80),
  internalSku: optionalTrimmed(120),
  vendorSku: optionalTrimmed(160),
  costAmount: z.number().int().min(0).default(0),
  currency: z.string().trim().length(3).default("USD"),
  discountBasisPoints: z.number().int().min(0).max(10000).optional().nullable().transform((value) => value ?? null),
  minimumOrderQuantity: z.number().int().positive().max(100000).default(1),
  casePackQuantity: z.number().int().positive().max(100000).default(1),
  returnability: purchasingReturnabilitySchema.default("unknown"),
  expectedLeadTimeDays: z.number().int().min(0).max(3650).optional().nullable().transform((value) => value ?? null),
  preferred: z.boolean().default(false),
  effectiveAt: z.string().datetime().optional().nullable().transform((value) => value || null),
  expiresAt: z.string().datetime().optional().nullable().transform((value) => value || null),
  notes: optionalTrimmed(2000),
  active: z.boolean().default(true),
});

export const purchasingVendorProductSaveSchema = purchasingVendorProductBaseSchema.refine((value) => value.editionId || value.variationId || value.isbn13 || value.isbn10 || value.upcEanGtin || value.internalSku || value.vendorSku, "Provide at least one product identifier")
  .refine((value) => !value.effectiveAt || !value.expiresAt || value.expiresAt > value.effectiveAt, "Expiration must be after effective date");

export const purchasingVendorProductPatchSchema = purchasingVendorProductBaseSchema.partial();

const purchasingPoLineInputSchema = z.object({
  id: idField.optional(),
  vendorProductId: idField.optional().nullable().transform((value) => value || null),
  editionId: idField.optional().nullable().transform((value) => value || null),
  variationId: idField.optional().nullable().transform((value) => value || null),
  isbn13: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid ISBN-13").optional().nullable().transform((value) => value || null),
  isbn10: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid ISBN-10").optional().nullable().transform((value) => value || null),
  upcEanGtin: optionalTrimmed(80),
  internalSku: optionalTrimmed(120),
  vendorSku: optionalTrimmed(160),
  titleSnapshot: optionalTrimmed(300),
  requestedQuantity: z.number().int().positive().max(100000),
  expectedUnitCostAmount: z.number().int().min(0).default(0),
  currency: z.string().trim().length(3).default("USD"),
  backorderPolicy: z.enum(["order_default", "allow", "cancel", "ship_available"]).default("order_default"),
  requestedShipDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  cancelIfNotFilledDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  doNotExceedUnitCostAmount: z.number().int().min(0).optional().nullable().transform((value) => value ?? null),
  allowSubstitutions: z.boolean().optional().nullable().transform((value) => value ?? null),
  lineNotes: optionalTrimmed(2000),
  sourceDemand: z.array(z.record(z.unknown())).default([]),
}).refine((value) => value.vendorProductId || value.editionId || value.variationId || value.isbn13 || value.isbn10 || value.upcEanGtin || value.internalSku || value.vendorSku, "Provide a product reference for each line");

export const purchasingPoCreateSchema = z.object({
  vendorId: idField,
  vendorAccountId: idField.optional().nullable().transform((value) => value || null),
  shipToLocationId: idField.default("loc-main"),
  billToLocationId: idField.optional().nullable().transform((value) => value || null),
  poType: purchasingPoTypeSchema.default("manual"),
  backorderPolicy: purchasingBackorderPolicySchema.default("allow"),
  requestedShipDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  cancelIfNotFilledDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  doNotExceedCostAmount: z.number().int().min(0).optional().nullable().transform((value) => value ?? null),
  currency: z.string().trim().length(3).default("USD"),
  allowSubstitutions: z.boolean().default(false),
  fulfillmentPreference: z.enum(["consolidate", "ship_immediately"]).default("consolidate"),
  externalReference: optionalTrimmed(200),
  headerNotes: optionalTrimmed(4000),
  sourceDemand: z.array(z.record(z.unknown())).default([]),
  lines: z.array(purchasingPoLineInputSchema).min(1).max(500),
  idempotencyKey: z.string().min(8).optional(),
});

export const purchasingPoUpdateSchema = purchasingPoCreateSchema.omit({ vendorId: true, idempotencyKey: true }).partial().extend({
  expectedVersion: z.number().int().positive(),
  vendorId: idField.optional(),
  lines: z.array(purchasingPoLineInputSchema).min(1).max(500).optional(),
});

export const purchasingPoTransitionSchema = z.object({
  expectedVersion: z.number().int().positive(),
  idempotencyKey: z.string().min(8),
  note: z.string().trim().max(1000).optional(),
});

export const purchasingReceivingPreviewSchema = z.object({
  lines: z.array(z.object({
    purchaseOrderLineId: idField.optional().nullable().transform((value) => value || null),
    variationId: idField.optional().nullable().transform((value) => value || null),
    receivedQuantity: z.number().int().min(0).max(100000).default(0),
    damagedQuantity: z.number().int().min(0).max(100000).default(0),
    shortageQuantity: z.number().int().min(0).max(100000).default(0),
    overageQuantity: z.number().int().min(0).max(100000).default(0),
    unexpected: z.boolean().default(false),
    receivedUnitCostAmount: z.number().int().min(0).default(0),
    currency: z.string().trim().length(3).default("USD"),
    customerRequestId: idField.optional().nullable().transform((value) => value || null),
    notes: optionalTrimmed(1000),
  })).min(1).max(500),
});

export const purchasingReceivingPostSchema = purchasingReceivingPreviewSchema.extend({
  locationId: idField.default("loc-main"),
  shipmentReference: optionalTrimmed(200),
  notes: optionalTrimmed(2000),
  idempotencyKey: z.string().min(8),
});

export const purchasingReceivingCorrectionSchema = z.object({
  receivingLineId: idField,
  quantityDelta: z.number().int().refine((value) => value !== 0, "Correction must change quantity"),
  unitCostAmount: z.number().int().min(0).default(0),
  reason: z.string().trim().min(1).max(1000),
  idempotencyKey: z.string().min(8),
});

const purchasingAsnLineSchema = z.object({
  poNumber: z.string().trim().min(1).max(80),
  purchaseOrderLineId: idField.optional().nullable().transform((value) => value || null),
  isbn13: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid ISBN-13").optional().nullable().transform((value) => value || null),
  vendorSku: optionalTrimmed(160),
  title: optionalTrimmed(400),
  shippedQuantity: z.number().int().min(0).max(100000),
  unitCostAmount: z.number().int().min(0).optional().nullable().transform((value) => value ?? null),
  cartonBarcode: optionalTrimmed(160),
  customerRequestId: idField.optional().nullable().transform((value) => value || null),
});

export const purchasingAsnInboundSchema = z.object({
  providerId: idField,
  environment: z.enum(["test", "production"]).default("test"),
  rawPayloadText: z.string().min(1).max(500000),
  externalDocumentId: optionalTrimmed(160),
  interchangeControlNumber: optionalTrimmed(160),
  supplierShipmentNumber: z.string().trim().min(1).max(160),
  vendorId: idField,
  vendorAccountId: idField.optional().nullable().transform((value) => value || null),
  originWarehouse: optionalTrimmed(160),
  destinationLocationId: idField.default("loc-main"),
  carrier: optionalTrimmed(120),
  shippingService: optionalTrimmed(120),
  trackingNumbers: z.array(z.string().trim().min(1).max(160)).max(20).default([]),
  estimatedShipDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  estimatedDeliveryDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  actualShipDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  actualDeliveryDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  packingSlipNumber: optionalTrimmed(160),
  freightAmount: z.number().int().min(0).optional().nullable().transform((value) => value ?? null),
  currency: z.string().trim().length(3).default("USD"),
  replacesShipmentId: idField.optional().nullable().transform((value) => value || null),
  lines: z.array(purchasingAsnLineSchema).min(1).max(1000),
  idempotencyKey: z.string().min(8),
});

export const purchasingCartonReceiveSchema = z.object({
  cartonBarcode: z.string().trim().min(1).max(160),
  locationId: idField.default("loc-main"),
  notes: optionalTrimmed(2000),
  lines: z.array(z.object({
    shipmentLineId: idField,
    receivedQuantity: z.number().int().min(0).max(100000).default(0),
    damagedQuantity: z.number().int().min(0).max(100000).default(0),
    shortageQuantity: z.number().int().min(0).max(100000).default(0),
    overageQuantity: z.number().int().min(0).max(100000).default(0),
    receivedUnitCostAmount: z.number().int().min(0).default(0),
    customerRequestId: idField.optional().nullable().transform((value) => value || null),
  })).min(1).max(500),
  idempotencyKey: z.string().min(8),
});

export const purchasingShipmentDiscrepancyResolutionSchema = z.object({
  status: z.enum(["supplier_followup", "resolved", "dismissed"]),
  resolutionReason: z.string().trim().min(1).max(1000),
  supplierFollowupStatus: optionalTrimmed(160),
  idempotencyKey: z.string().min(8),
});

export const purchasingInvoiceStatusSchema = z.enum(["received", "matching", "matched", "review_required", "approved", "exported", "partially_paid", "paid", "disputed", "canceled"]);
export const purchasingCreditMemoTypeSchema = z.enum(["damaged_copy", "short_shipment", "return_credit", "freight_adjustment", "promotional_discount", "debit_adjustment", "credit_adjustment", "invoice_level", "line_level"]);

const purchasingInvoiceLineInboundSchema = z.object({
  poNumber: optionalTrimmed(80),
  purchaseOrderLineId: idField.optional().nullable().transform((value) => value || null),
  shipmentId: idField.optional().nullable().transform((value) => value || null),
  receivingSessionId: idField.optional().nullable().transform((value) => value || null),
  receivingLineId: idField.optional().nullable().transform((value) => value || null),
  isbn13: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid ISBN-13").optional().nullable().transform((value) => value || null),
  vendorSku: optionalTrimmed(160),
  description: z.string().trim().min(1).max(500),
  quantity: z.number().int().min(0).max(100000),
  unitCostAmount: z.number().int().min(0).max(100000000),
  discountAmount: z.number().int().min(0).max(100000000).default(0),
  taxAmount: z.number().int().min(0).max(100000000).default(0),
  freightAmount: z.number().int().min(0).max(100000000).default(0),
  handlingAmount: z.number().int().min(0).max(100000000).default(0),
  otherFeeAmount: z.number().int().min(0).max(100000000).default(0),
  creditAmount: z.number().int().min(0).max(100000000).default(0),
  adjustmentAmount: z.number().int().min(-100000000).max(100000000).default(0),
});

export const purchasingInvoiceInboundSchema = z.object({
  providerId: idField,
  environment: z.enum(["test", "production"]).default("test"),
  rawPayloadText: z.string().min(1).max(500000),
  externalDocumentId: optionalTrimmed(160),
  interchangeControlNumber: optionalTrimmed(160),
  invoiceNumber: z.string().trim().min(1).max(160),
  invoiceDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  dueDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  paymentTerms: optionalTrimmed(200),
  vendorId: idField,
  vendorAccountId: idField.optional().nullable().transform((value) => value || null),
  supplierAccountReference: optionalTrimmed(160),
  currency: z.string().trim().length(3).default("USD"),
  freightAmount: z.number().int().min(0).max(100000000).default(0),
  handlingAmount: z.number().int().min(0).max(100000000).default(0),
  otherFeeAmount: z.number().int().min(0).max(100000000).default(0),
  discountAmount: z.number().int().min(0).max(100000000).default(0),
  taxAmount: z.number().int().min(0).max(100000000).default(0),
  creditAmount: z.number().int().min(0).max(100000000).default(0),
  adjustmentAmount: z.number().int().min(-100000000).max(100000000).default(0),
  invoiceTotalAmount: z.number().int().min(0).max(1000000000).optional(),
  originalInvoiceId: idField.optional().nullable().transform((value) => value || null),
  correctionType: z.enum(["original", "corrected", "replacement", "canceled"]).default("original"),
  lines: z.array(purchasingInvoiceLineInboundSchema).min(1).max(1000),
  idempotencyKey: z.string().min(8),
});

export const purchasingInvoiceMatchSchema = z.object({
  costToleranceAmount: z.number().int().min(0).max(100000).default(1),
  roundingToleranceAmount: z.number().int().min(0).max(100000).default(1),
  quantityTolerance: z.number().int().min(0).max(1000).default(0),
  idempotencyKey: z.string().min(8).optional(),
});

export const purchasingInvoiceTransitionSchema = z.object({
  status: z.enum(["approved", "disputed", "canceled", "partially_paid", "paid"]),
  reason: z.string().trim().min(1).max(1000),
  idempotencyKey: z.string().min(8),
});

export const purchasingInvoiceDiscrepancyResolutionSchema = z.object({
  status: z.enum(["supplier_followup", "resolved", "dismissed"]),
  resolutionReason: z.string().trim().min(1).max(1000),
  idempotencyKey: z.string().min(8),
});

export const purchasingCreditMemoCreateSchema = z.object({
  vendorId: idField,
  vendorAccountId: idField.optional().nullable().transform((value) => value || null),
  creditNumber: z.string().trim().min(1).max(160),
  creditDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  originalInvoiceId: idField.optional().nullable().transform((value) => value || null),
  receivingDiscrepancyId: idField.optional().nullable().transform((value) => value || null),
  creditType: purchasingCreditMemoTypeSchema,
  amount: z.number().int().min(0).max(100000000),
  currency: z.string().trim().length(3).default("USD"),
  notes: optionalTrimmed(2000),
  idempotencyKey: z.string().min(8),
});

export const purchasingAccountingExportSchema = z.object({
  invoiceIds: z.array(idField).default([]),
  creditMemoIds: z.array(idField).default([]),
  adapterKey: z.enum(["generic_csv"]).default("generic_csv"),
  includePreviouslyExported: z.boolean().default(false),
  reexportReason: optionalTrimmed(1000),
  idempotencyKey: z.string().min(8),
}).refine((value) => value.invoiceIds.length > 0 || value.creditMemoIds.length > 0, "Select invoices or credit memos to export");

export const purchasingSupplierReturnReasonSchema = z.enum(["overstock", "damaged_in_store", "received_damaged", "defective", "incorrect_item", "unauthorized_substitution", "recall", "event_overstock", "vendor_requested_return", "other"]);
export const purchasingSupplierReturnStatusSchema = z.enum(["draft", "authorization_requested", "authorized", "authorization_rejected", "authorization_expired", "staged", "packed", "shipped", "partially_credited", "credited", "disputed", "canceled", "corrected", "closed"]);
export const purchasingSupplierReturnMethodSchema = z.enum(["electronic", "email", "phone", "portal", "manual"]);

const purchasingSupplierReturnLineSchema = z.object({
  sourceReceivingLineId: idField.optional().nullable().transform((value) => value || null),
  sourcePurchaseOrderLineId: idField.optional().nullable().transform((value) => value || null),
  vendorProductId: idField.optional().nullable().transform((value) => value || null),
  editionId: idField.optional().nullable().transform((value) => value || null),
  variationId: idField,
  isbn13: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid ISBN").optional().nullable().transform((value) => value || null),
  vendorSku: optionalTrimmed(120),
  titleSnapshot: z.string().trim().min(1).max(400),
  quantity: z.number().int().min(1).max(100000),
  reason: purchasingSupplierReturnReasonSchema,
  reasonDetail: optionalTrimmed(1000),
  condition: z.enum(["sellable", "damaged", "defective", "incorrect", "recall", "other"]).default("sellable"),
  manualOverride: z.boolean().default(false),
  manualOverrideReason: optionalTrimmed(1000),
  expectedUnitCreditAmount: z.number().int().min(0).max(100000000).default(0),
  currency: z.string().trim().length(3).default("USD"),
}).refine((value) => value.reason !== "other" || value.reasonDetail, "Other return reasons require an explanation")
  .refine((value) => !value.manualOverride || value.manualOverrideReason, "Manual overrides require a documented reason");

export const purchasingSupplierReturnEligibilitySchema = z.object({
  vendorId: idField,
  vendorAccountId: idField.optional().nullable().transform((value) => value || null),
  locationId: idField.default("loc-main"),
  variationId: idField.optional().nullable().transform((value) => value || null),
  isbn13: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid ISBN").optional().nullable().transform((value) => value || null),
  quantity: z.number().int().min(1).max(100000).default(1),
  reason: purchasingSupplierReturnReasonSchema.default("overstock"),
  includeManualOverride: z.boolean().default(false),
});

export const purchasingSupplierReturnSuggestionSchema = z.object({
  vendorId: idField.optional().nullable().transform((value) => value || null),
  vendorAccountId: idField.optional().nullable().transform((value) => value || null),
  locationId: idField.default("loc-main"),
  minAgeDays: z.number().int().min(0).max(3650).default(120),
  maxSalesVelocityPerWeek: z.number().min(0).max(100000).default(0.25),
  minOnHandQuantity: z.number().int().min(1).max(100000).default(1),
  includeConditional: z.boolean().default(false),
  limit: z.number().int().min(1).max(200).default(50),
});

export const purchasingSupplierReturnCreateSchema = z.object({
  vendorId: idField,
  vendorAccountId: idField.optional().nullable().transform((value) => value || null),
  sourceLocationId: idField.default("loc-main"),
  returnMethod: purchasingSupplierReturnMethodSchema.default("manual"),
  authorizationNumber: optionalTrimmed(160),
  authorizationExpiresAt: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  shippingInstructions: optionalTrimmed(2000),
  requiredPaperwork: z.array(z.string().trim().min(1).max(160)).default([]),
  restockingFeeAmount: z.number().int().min(0).max(100000000).default(0),
  notes: optionalTrimmed(2000),
  lines: z.array(purchasingSupplierReturnLineSchema).min(1).max(500),
  idempotencyKey: z.string().min(8),
});

export const purchasingSupplierReturnAuthorizationSchema = z.object({
  status: z.enum(["authorization_requested", "authorized", "authorization_rejected", "authorization_expired"]),
  authorizationNumber: optionalTrimmed(160),
  quantityAuthorizations: z.array(z.object({
    lineId: idField,
    authorizedQuantity: z.number().int().min(0).max(100000),
    rejectedQuantity: z.number().int().min(0).max(100000).default(0),
    expiredQuantity: z.number().int().min(0).max(100000).default(0),
  })).default([]),
  authorizationExpiresAt: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  shippingInstructions: optionalTrimmed(2000),
  restockingFeeAmount: z.number().int().min(0).max(100000000).optional(),
  expectedCreditAmount: z.number().int().min(0).max(100000000).optional(),
  note: z.string().trim().min(1).max(1000),
  idempotencyKey: z.string().min(8),
}).refine((value) => value.status !== "authorized" || value.authorizationNumber, "Authorized returns require an authorization number");

export const purchasingSupplierReturnStageSchema = z.object({
  lineQuantities: z.array(z.object({ lineId: idField, quantity: z.number().int().min(0).max(100000) })).min(1).max(500),
  note: z.string().trim().min(1).max(1000),
  idempotencyKey: z.string().min(8),
});

export const purchasingSupplierReturnShipmentSchema = z.object({
  cartons: z.array(z.object({
    cartonNumber: z.string().trim().min(1).max(80),
    weightOunces: z.number().int().min(0).max(1000000).optional().nullable().transform((value) => value ?? null),
    carrier: optionalTrimmed(80),
    trackingNumber: optionalTrimmed(160),
  })).default([]),
  carrier: optionalTrimmed(80),
  trackingNumber: optionalTrimmed(160),
  shipmentDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  note: z.string().trim().min(1).max(1000),
  idempotencyKey: z.string().min(8),
});

export const purchasingSupplierReturnCancelSchema = z.object({
  reason: z.string().trim().min(1).max(1000),
  releaseStagedInventory: z.boolean().default(true),
  idempotencyKey: z.string().min(8),
});

export const purchasingSupplierReturnCreditReconcileSchema = z.object({
  creditMemoId: idField.optional().nullable().transform((value) => value || null),
  lineId: idField.optional().nullable().transform((value) => value || null),
  actualCreditAmount: z.number().int().min(0).max(100000000),
  quantityDifference: z.number().int().min(-100000).max(100000).default(0),
  unitCreditDifferenceAmount: z.number().int().min(-100000000).max(100000000).default(0),
  feesOrDeductionsAmount: z.number().int().min(0).max(100000000).default(0),
  status: z.enum(["partial_credit", "credited", "missing_credit", "disputed", "resolved"]),
  resolutionReason: z.string().trim().min(1).max(1000),
  idempotencyKey: z.string().min(8),
});

export const purchasingPoChangeRequestCreateSchema = z.object({
  changeType: purchasingPoChangeTypeSchema,
  purchaseOrderLineId: idField.optional().nullable().transform((value) => value || null),
  requestedQuantity: z.number().int().min(0).max(100000).optional(),
  requestedShipDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  cancelIfNotFilledDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  backorderPolicy: z.enum(["allow", "cancel", "ship_available"]).optional(),
  shippingInstructions: z.string().trim().max(1000).optional().nullable().transform((value) => value || null),
  replacementEditionId: idField.optional().nullable().transform((value) => value || null),
  replacementVariationId: idField.optional().nullable().transform((value) => value || null),
  replacementIsbn13: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid replacement ISBN-13").optional().nullable().transform((value) => value || null),
  replacementTitle: z.string().trim().max(400).optional().nullable().transform((value) => value || null),
  replacementCostAmount: z.number().int().min(0).optional().nullable().transform((value) => value ?? null),
  reason: z.string().trim().min(1).max(1000),
  supplierTransmissionMethod: purchasingPoChangeTransmissionSchema.default("manual_contact"),
  externalReference: z.string().trim().max(160).optional().nullable().transform((value) => value || null),
  idempotencyKey: z.string().min(8),
}).refine((value) => !["cancel_line", "reduce_quantity", "increase_quantity", "change_backorder_preference", "replace_edition", "close_backorder"].includes(value.changeType) || value.purchaseOrderLineId, "Select a purchase-order line for this change")
  .refine((value) => !["reduce_quantity", "increase_quantity"].includes(value.changeType) || value.requestedQuantity !== undefined, "Quantity changes require a requested quantity")
  .refine((value) => value.changeType !== "change_requested_ship_date" || value.requestedShipDate, "Requested ship-date changes require a date")
  .refine((value) => value.changeType !== "change_cancel_if_not_filled_date" || value.cancelIfNotFilledDate, "Cancel-if-not-filled changes require a date")
  .refine((value) => value.changeType !== "change_backorder_preference" || value.backorderPolicy, "Backorder preference changes require a policy")
  .refine((value) => value.changeType !== "change_shipping_instructions" || value.shippingInstructions, "Shipping instruction changes require instructions")
  .refine((value) => value.changeType !== "replace_edition" || value.replacementEditionId || value.replacementVariationId || value.replacementIsbn13 || value.replacementTitle, "Substitution review requires a proposed edition or ISBN");

export const purchasingPoChangeRequestTransitionSchema = z.object({
  note: z.string().trim().max(1000).optional(),
  idempotencyKey: z.string().min(8),
});

export const purchasingPoChangeRequestResponseSchema = z.object({
  status: z.enum(["supplier_received", "accepted", "partially_accepted", "rejected", "failed"]),
  supplierResponse: z.string().trim().min(1).max(2000),
  externalReference: z.string().trim().max(160).optional().nullable().transform((value) => value || null),
  externalControlNumber: z.string().trim().max(160).optional().nullable().transform((value) => value || null),
  relatedMessageId: z.string().trim().max(160).optional().nullable().transform((value) => value || null),
  acceptedValue: z.record(z.unknown()).default({}),
  idempotencyKey: z.string().min(8),
});

export const purchasingBackorderPolicySaveSchema = z.object({
  locationId: idField.default("loc-main"),
  retainDays: z.number().int().min(0).max(3650).default(90),
  customerOrderPriority: z.boolean().default(true),
  autoCloseExpired: z.boolean().default(false),
  notifyOverdueAfterDays: z.number().int().min(0).max(3650).default(14),
});

export const purchasingBackorderBatchActionSchema = z.object({
  purchaseOrderLineIds: z.array(idField).min(1).max(200),
  action: z.enum(["close_backorder", "cancel_backorder"]),
  reason: z.string().trim().min(1).max(1000),
  supplierTransmissionMethod: purchasingPoChangeTransmissionSchema.default("manual_contact"),
  idempotencyKey: z.string().min(8),
});

export const purchasingReplenishmentPreviewSchema = z.object({
  locationId: idField.default("loc-main"),
  lookbackDays: z.number().int().min(1).max(730).default(56),
  targetWeeksSupply: z.number().int().min(1).max(104).default(6),
  includeCustomerRequests: z.boolean().default(true),
  includePreorders: z.boolean().default(true),
  includeEventDemand: z.boolean().default(true),
  idempotencyKey: z.string().min(8).optional(),
});

export const purchasingReplenishmentSuggestionPatchSchema = z.object({
  finalQuantity: z.number().int().min(0).max(100000).optional(),
  status: z.enum(["pending", "excluded", "deferred", "approved"]).optional(),
  overrideReason: optionalTrimmed(1000),
}).refine((value) => value.finalQuantity !== undefined || value.status !== undefined || value.overrideReason !== undefined, "Provide a replenishment suggestion change");

export const purchasingReplenishmentCreatePoSchema = z.object({
  suggestionIds: z.array(idField).min(1).max(500),
  shipToLocationId: idField.default("loc-main"),
  billToLocationId: idField.optional().nullable().transform((value) => value || null),
  submitForApproval: z.boolean().default(false),
  idempotencyKey: z.string().min(8),
});

export const purchasingExportTemplateSaveSchema = z.object({
  vendorId: idField.optional().nullable().transform((value) => value || null),
  name: z.string().trim().min(1).max(160),
  format: z.enum(["csv", "spreadsheet_csv"]).default("csv"),
  columnMapping: z.array(z.object({
    header: z.string().trim().min(1).max(120),
    field: z.string().trim().min(1).max(120),
  })).min(1).max(40).default([
    { header: "PO Number", field: "poNumber" },
    { header: "Vendor SKU", field: "vendorSku" },
    { header: "ISBN-13", field: "isbn13" },
    { header: "Title", field: "title" },
    { header: "Quantity", field: "quantity" },
    { header: "Unit Cost", field: "unitCost" },
  ]),
});

export const purchasingPoExportSchema = z.object({
  templateId: idField.optional().nullable().transform((value) => value || null),
  exportFormat: z.enum(["csv", "spreadsheet_csv", "print_html"]).default("csv"),
  deliveryMethod: z.enum(["download", "email_prepared", "email_sent", "manual_upload"]).default("download"),
  note: optionalTrimmed(1000),
  idempotencyKey: z.string().min(8),
});

export const purchasingManualAcknowledgmentSchema = z.object({
  expectedVersion: z.number().int().positive(),
  externalReference: optionalTrimmed(200),
  note: optionalTrimmed(1000),
  lines: z.array(z.object({
    purchaseOrderLineId: idField,
    acknowledgedQuantity: z.number().int().min(0).max(100000),
    backorderedQuantity: z.number().int().min(0).max(100000).default(0),
    canceledQuantity: z.number().int().min(0).max(100000).default(0),
    expectedShipDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  })).min(1).max(500),
  idempotencyKey: z.string().min(8),
});

export const purchasingAckImportSchema = z.object({
  templateId: idField.optional().nullable().transform((value) => value || null),
  sourceText: z.string().trim().min(1).max(2_000_000),
  idempotencyKey: z.string().min(8),
});

export const purchasingApprovalPolicySchema = z.object({
  enabled: z.boolean().default(true),
  requireApprovalAboveAmount: z.number().int().min(0).default(0),
  managerApprovalLimitAmount: z.number().int().min(0).optional().nullable().transform((value) => value ?? null),
});

export const purchasingApprovalDecisionSchema = z.object({
  expectedVersion: z.number().int().positive(),
  reason: z.string().trim().min(1).max(1000),
  idempotencyKey: z.string().min(8),
});

export const purchasingSpecialOrderCreateSchema = z.object({
  customerId: idField,
  title: z.string().trim().min(1).max(300),
  author: optionalTrimmed(200),
  isbn: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid ISBN").optional().nullable().transform((value) => value || null),
  variationId: idField.optional().nullable().transform((value) => value || null),
  quantity: z.number().int().positive().max(1000).default(1),
  fulfillmentPreference: z.enum(["pickup", "ship_to_customer", "hold_for_customer"]).default("pickup"),
  promisedDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  estimatedDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  depositStatus: z.enum(["not_required", "unpaid", "partial", "paid", "refunded"]).default("not_required"),
  notes: optionalTrimmed(2000),
  idempotencyKey: z.string().min(8).optional(),
});

export const purchasingSpecialOrderUpdateSchema = z.object({
  purchasingStatus: z.enum(["needed", "ordered", "acknowledged", "backordered", "shipped", "received", "ready_for_pickup", "fulfilled", "canceled"]).optional(),
  status: customerRequestStatusSchema.optional(),
  quantity: z.number().int().positive().max(1000).optional(),
  promisedDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  estimatedDate: z.string().trim().max(40).optional().nullable().transform((value) => value || null),
  depositStatus: z.enum(["not_required", "unpaid", "partial", "paid", "refunded"]).optional(),
  notes: optionalTrimmed(2000),
  idempotencyKey: z.string().min(8).optional(),
});

export const purchasingListQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  status: z.string().trim().max(80).optional(),
  vendorId: z.string().trim().max(160).optional(),
  cursor: z.string().trim().max(160).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const integrationProviderTypeSchema = z.enum(["book_industry_edi", "direct_vendor_api", "sftp", "van", "as2", "local_test", "other"]);
export const integrationEnvironmentSchema = z.enum(["test", "production"]);
export const integrationTransportSchema = z.enum(["local_test", "ftp_mailbox", "sftp", "van", "as2", "api"]);
export const integrationMessageTypeSchema = z.enum(["purchase_order", "purchase_order_line", "technical_ack", "order_response", "response_line", "shipment", "shipment_line", "invoice", "invoice_line", "trading_partner", "location", "product_identifier", "party_identifier", "return_adjustment"]);
export const integrationProcessingStatusSchema = z.enum(["validated", "queued", "transmitting", "transmitted", "received", "processed", "pending_review", "failed", "terminal_error", "duplicate", "dead_letter"]);
export const integrationCapabilityNameSchema = z.enum([
  "availabilityLookup",
  "accountSpecificPricing",
  "electronicPurchaseOrder",
  "technicalAcknowledgment",
  "businessAcknowledgment",
  "purchaseOrderChanges",
  "cancellations",
  "shipmentNotices",
  "cartonDetails",
  "tracking",
  "invoices",
  "credits",
  "returns",
  "directToCustomerFulfillment",
  "testEnvironment",
]);
export const integrationCapabilityModelSchema = z.object({
  availabilityLookup: z.boolean().default(false),
  accountSpecificPricing: z.boolean().default(false),
  electronicPurchaseOrder: z.boolean().default(false),
  technicalAcknowledgment: z.boolean().default(false),
  businessAcknowledgment: z.boolean().default(false),
  purchaseOrderChanges: z.boolean().default(false),
  cancellations: z.boolean().default(false),
  shipmentNotices: z.boolean().default(false),
  cartonDetails: z.boolean().default(false),
  tracking: z.boolean().default(false),
  invoices: z.boolean().default(false),
  credits: z.boolean().default(false),
  returns: z.boolean().default(false),
  directToCustomerFulfillment: z.boolean().default(false),
  testEnvironment: z.boolean().default(true),
  supportedIdentifiers: z.array(z.enum(["isbn13", "isbn10", "upc", "ean", "gtin", "internal_sku", "vendor_sku"])).default(["isbn13", "isbn10", "vendor_sku"]),
  supportedCurrencies: z.array(z.string().trim().length(3)).default(["USD"]),
  supportedLocations: z.array(z.string().trim().min(1).max(120)).default([]),
  supportedWarehouses: z.array(z.string().trim().min(1).max(120)).default([]),
}).passthrough();

export const integrationProviderSaveSchema = z.object({
  providerKey: z.string().trim().min(1).max(80).regex(/^[a-z0-9_-]+$/).default("pubnet"),
  displayName: z.string().trim().min(1).max(160).default("Pubnet"),
  providerType: integrationProviderTypeSchema.default("book_industry_edi"),
  status: z.enum(["configured", "testing", "active", "disabled"]).default("testing"),
  environment: integrationEnvironmentSchema.default("test"),
  adapterVersion: z.string().trim().min(1).max(80).default("pubnet-x12-3060-v1"),
  mapVersion: z.string().trim().min(1).max(80).default("pubnet-850-3060-2020"),
  capabilityModelVersion: z.string().trim().min(1).max(80).default("integration-capabilities-v1"),
  capabilities: integrationCapabilityModelSchema.partial().default({ electronicPurchaseOrder: true, businessAcknowledgment: true, technicalAcknowledgment: true, shipmentNotices: true, invoices: true, credits: true, testEnvironment: true }),
  validationRules: z.record(z.unknown()).default({}),
  featureFlags: z.record(z.unknown()).default({}),
  certificationChecklist: z.array(z.record(z.unknown())).default([]),
  retentionPolicy: z.record(z.unknown()).default({}),
  supportNotes: optionalTrimmed(4000),
  incidentTimeline: z.array(z.record(z.unknown())).default([]),
  killSwitchEnabled: z.boolean().default(false),
  tenantKillSwitchEnabled: z.boolean().default(false),
  rolloutPercent: z.number().int().min(0).max(100).default(0),
  rateLimitPerMinute: z.number().int().min(1).max(10000).default(30),
  circuitState: z.enum(["closed", "open", "half_open"]).default("closed"),
  circuitOpenUntil: z.string().datetime().optional().nullable().transform((value) => value || null),
  timeoutMs: z.number().int().min(1000).max(120000).default(15000),
});
export const integrationProviderPatchSchema = integrationProviderSaveSchema.partial();

export const integrationCredentialSaveSchema = z.object({
  vendorId: idField.optional().nullable().transform((value) => value || null),
  vendorAccountId: idField.optional().nullable().transform((value) => value || null),
  credentialName: z.string().trim().min(1).max(160),
  credentialType: z.enum(["pubnet_mailbox", "api_key", "sftp_password", "sftp_private_key", "as2_certificate", "van_mailbox", "basic_auth", "other"]),
  secretBindingName: z.string().trim().min(1).max(160).regex(/^[A-Z0-9_]+$/),
  publicMetadata: z.record(z.unknown()).default({}),
  status: z.enum(["active", "rotation_due", "disabled"]).default("active"),
  rotationDueAt: z.string().datetime().optional().nullable().transform((value) => value || null),
});

const integrationTradingPartnerBaseSchema = z.object({
  providerId: idField,
  vendorId: idField,
  vendorAccountId: idField.optional().nullable().transform((value) => value || null),
  environment: integrationEnvironmentSchema.default("test"),
  status: z.enum(["configured", "testing", "active", "disabled"]).default("testing"),
  externalPartyId: optionalTrimmed(160),
  san: optionalTrimmed(20),
  pubnetId: optionalTrimmed(80),
  x12SenderId: optionalTrimmed(80),
  x12ReceiverId: optionalTrimmed(80),
  defaultTransport: integrationTransportSchema.default("local_test"),
  documentFormat: z.string().trim().min(1).max(80).default("x12_3060"),
  capabilities: integrationCapabilityModelSchema.partial().default({ electronicPurchaseOrder: true, businessAcknowledgment: true }),
  warehouseMappings: z.record(z.unknown()).default({}),
  addressMappings: z.record(z.unknown()).default({}),
  shippingCodeMappings: z.record(z.unknown()).default({}),
  unitOfMeasureMappings: z.record(z.unknown()).default({}),
  paymentTerms: optionalTrimmed(120),
  validationRules: z.record(z.unknown()).default({}),
  effectiveFrom: z.string().datetime().optional().nullable().transform((value) => value || null),
  effectiveUntil: z.string().datetime().optional().nullable().transform((value) => value || null),
  certificationStatus: z.enum(["not_started", "testing", "certified", "blocked"]).default("not_started"),
  certificationNotes: optionalTrimmed(2000),
});
export const integrationTradingPartnerSaveSchema = integrationTradingPartnerBaseSchema.refine((value) => value.san || value.pubnetId || value.x12ReceiverId || value.externalPartyId, "Provide at least one trading-party identifier");
export const integrationTradingPartnerPatchSchema = integrationTradingPartnerBaseSchema.omit({ providerId: true, vendorId: true }).partial();

export const integrationListQuerySchema = z.object({
  providerId: z.string().trim().max(160).optional(),
  purchaseOrderId: z.string().trim().max(160).optional(),
  status: z.string().trim().max(80).optional(),
  direction: z.enum(["outbound", "inbound"]).optional(),
  cursor: z.string().trim().max(160).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const integrationPoElectronicSubmitSchema = z.object({
  providerId: idField,
  tradingPartnerId: idField.optional().nullable().transform((value) => value || null),
  previewOnly: z.boolean().default(false),
  idempotencyKey: z.string().min(8),
});

export const integrationMessageRetrySchema = z.object({
  idempotencyKey: z.string().min(8),
  reason: z.string().trim().min(1).max(1000),
});

export const integrationInboundMessageSchema = z.object({
  providerId: idField,
  environment: integrationEnvironmentSchema.default("test"),
  messageType: integrationMessageTypeSchema.default("order_response"),
  rawPayloadText: z.string().trim().min(1).max(5_000_000),
  externalDocumentId: optionalTrimmed(160),
  interchangeControlNumber: optionalTrimmed(160),
  originalDocumentId: optionalTrimmed(160),
  idempotencyKey: z.string().min(8),
});

export const integrationSimulatedResponseSchema = z.object({
  lineStatus: z.enum(["accepted", "backordered", "canceled", "rejected", "quantity_changed", "cost_changed", "substituted"]).default("accepted"),
  changedQuantity: z.number().int().min(0).max(100000).optional(),
  changedUnitCostAmount: z.number().int().min(0).optional(),
  replacementIsbn13: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid ISBN-13").optional(),
  idempotencyKey: z.string().min(8),
});

export const integrationReviewResolutionSchema = z.object({
  resolution: z.enum(["resolved", "dismissed"]),
  reason: z.string().trim().min(1).max(1000),
  idempotencyKey: z.string().min(8),
});

export const importSourceTypeSchema = z.enum(["square_api", "square_csv", "basil", "generic_csv", "generic_xlsx"]);
export const importModeSchema = z.enum(["preview_only", "catalog_only", "add_to_stock", "replace_stock"]);
export const importJobStatusSchema = z.enum([
  "uploaded",
  "parsing",
  "parsed",
  "preview_ready",
  "awaiting_resolution",
  "ready_to_apply",
  "applying",
  "applied",
  "partially_applied",
  "failed",
  "reversing",
  "reversed",
  "reversal_blocked",
  "cancelled",
]);

export const normalizedImportContributorSchema = z.object({
  displayName: z.string().trim().min(1).max(200),
  role: z.string().trim().min(1).max(80).default("author"),
  displayOrder: z.number().int().min(0).default(0),
});

export const normalizedImportRowSchema = z.object({
  sourceRecordId: z.string().trim().max(200).optional(),
  workTitle: z.string().trim().min(1).max(300),
  subtitle: z.string().trim().max(300).optional(),
  description: z.string().trim().max(10000).optional(),
  contributors: z.array(normalizedImportContributorSchema).default([]),
  publisher: z.string().trim().max(200).optional(),
  imprint: z.string().trim().max(200).optional(),
  publicationDate: z.string().trim().max(40).optional(),
  language: z.string().trim().max(20).optional(),
  pageCount: z.number().int().positive().optional(),
  isbn10: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid ISBN-10").optional(),
  isbn13: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid ISBN-13").optional(),
  gtin: z.string().trim().max(80).optional(),
  otherIdentifiers: z.array(z.object({ type: z.string().trim().min(1).max(40), value: z.string().trim().min(1).max(100) })).default([]),
  format: z.string().trim().max(100).optional(),
  specialAttributes: specialEditionAttributesSchema.default({}),
  sku: z.string().trim().max(100).optional(),
  sourceBarcode: z.string().trim().max(120).optional(),
  storeBarcode: z.string().trim().max(120).optional(),
  trackingMode: trackingModeSchema.default("quantity"),
  condition: z.string().trim().max(80).optional(),
  conditionNotes: z.string().trim().max(1000).optional(),
  quantity: z.number().int().min(0).optional(),
  costAmount: z.number().int().min(0).optional(),
  listPriceAmount: z.number().int().min(0).optional(),
  sellingPriceAmount: z.number().int().min(0).optional(),
  currency: z.string().length(3).default("USD"),
  categories: z.array(z.string().trim().min(1).max(120)).default([]),
  imageUrls: z.array(z.string().url()).default([]),
  locationSource: z.string().trim().max(120).optional(),
  shelfSource: z.string().trim().max(120).optional(),
  onlineVisibility: z.boolean().default(false),
  externalReferences: z.array(z.object({
    provider: z.string().trim().min(1).max(80),
    entityType: z.string().trim().min(1).max(80),
    externalId: z.string().trim().min(1).max(200),
    metadata: z.record(z.unknown()).optional(),
  })).default([]),
  sourceMetadata: z.record(z.unknown()).default({}),
  warnings: z.array(z.string()).default([]),
});

export const importCreateSchema = z.object({
  sourceType: importSourceTypeSchema,
  importMode: importModeSchema.default("preview_only"),
  targetLocationId: z.string().trim().min(1).default("loc-main"),
  filename: z.string().trim().max(240).optional(),
  mimeType: z.string().trim().max(120).optional(),
  sourceText: z.string().max(15_000_000).optional(),
  fieldMapping: z.record(z.string()).default({}),
  conflictPolicies: z.record(z.unknown()).default({}),
  idempotencyKey: z.string().min(8).optional(),
}).refine((value) => value.sourceType === "square_api" || value.sourceText, "Source text is required for file imports");

export const importInspectSchema = z.object({
  sourceType: importSourceTypeSchema,
  filename: z.string().trim().max(240).optional(),
  mimeType: z.string().trim().max(120).optional(),
  sourceText: z.string().max(15_000_000),
  fieldMapping: z.record(z.string()).default({}),
}).refine((value) => value.sourceType !== "square_api", "File inspection requires an uploaded spreadsheet");

export const importPreviewSchema = z.object({
  importMode: importModeSchema.optional(),
  targetLocationId: z.string().trim().min(1).optional(),
  explicitReplaceStockConfirmation: z.boolean().default(false),
  enrichMetadata: z.boolean().default(false),
});

export const importApplySchema = z.object({
  idempotencyKey: z.string().min(8),
  explicitReplaceStockConfirmation: z.boolean().default(false),
});

export const importConflictResolutionSchema = z.object({
  conflicts: z.array(z.object({
    conflictId: z.string().min(1),
    selectedResolution: z.enum(["keep_local", "use_source", "fill_blank", "skip", "accept_mapping", "ignore"]).or(z.string().trim().min(1).max(80)),
    notes: z.string().trim().max(500).optional(),
  })).min(1),
});

export const importReversalSchema = z.object({
  idempotencyKey: z.string().min(8),
  reason: z.string().trim().min(1).max(500),
});

export const importFieldMappingSaveSchema = z.object({
  name: z.string().trim().min(1).max(120),
  sourceType: importSourceTypeSchema,
  sourceProfile: z.string().trim().max(120).optional(),
  mappingDefinition: z.record(z.string()),
});

export const receivingBatchStatusSchema = z.enum(["draft", "in_progress", "completed", "partially_completed", "cancelled", "correction_required", "corrected"]);
export const receivingLineStatusSchema = z.enum(["draft", "ready", "completed", "failed", "cancelled", "corrected"]);
export const labelBatchStatusSchema = z.enum(["draft", "ready", "generating", "generated", "partially_generated", "failed", "cancelled"]);
export const conditionCodeSchema = z.string().trim().min(1).max(80).regex(/^[a-z0-9_-]+$/);

export const inventoryDefaultsSchema = z.object({
  defaultLocationId: z.string().trim().min(1).default("loc-main"),
  defaultShelfId: z.string().trim().min(1).optional(),
  defaultFormatId: z.string().trim().min(1).optional(),
  defaultTrackingMode: trackingModeSchema.default("quantity"),
  defaultConditionId: z.string().trim().min(1).optional(),
  defaultCurrency: z.string().length(3).default("USD"),
  defaultLabelTemplateId: z.string().trim().min(1).optional(),
  autoGenerateBarcodes: z.boolean().default(true),
  barcodePrefix: z.string().trim().min(1).max(12).regex(/^[A-Z0-9-]+$/),
  barcodeMinWidth: z.number().int().min(3).max(18).default(6),
  receivingMetadataBehavior: z.enum(["disabled", "suggest", "fill_blank"]).default("suggest"),
  duplicateScanBehavior: z.enum(["increment_quantity", "warn"]).default("increment_quantity"),
  labelsPerCopy: z.number().int().min(0).max(20).default(1),
  requirePriceOnReceive: z.boolean().default(true),
  requireCostOnReceive: z.boolean().default(false),
  offerLabelBatchAfterReceiving: z.boolean().default(true),
});

export const operationalConditionCreateSchema = z.object({
  code: conditionCodeSchema,
  displayName: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).optional(),
  sortOrder: z.number().int().min(0).max(10000).default(100),
  active: z.boolean().default(true),
});

export const locationSaveSchema = z.object({
  id: z.string().trim().min(1).max(80).optional(),
  code: z.string().trim().min(1).max(40).regex(/^[A-Za-z0-9_-]+$/),
  name: z.string().trim().min(1).max(120),
  active: z.boolean().default(true),
});

export const shelfSaveSchema = z.object({
  id: z.string().trim().min(1).max(80).optional(),
  locationId: z.string().trim().min(1),
  code: z.string().trim().min(1).max(80),
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().trim().max(500).optional(),
  active: z.boolean().default(true),
});

export const formatSaveSchema = z.object({
  code: z.string().trim().min(1).max(80).regex(/^[a-z0-9_]+$/),
  displayName: z.string().trim().min(1).max(120),
  displayOrder: z.number().int().min(0).max(10000).default(100),
  active: z.boolean().default(true),
});

export const phase4ReceivingLineInputSchema = z.object({
  sourceIdentifier: z.string().trim().max(160).optional(),
  isbn: z.string().transform(normalizeIsbn).refine((value) => !value || isValidIsbn(value), "Invalid ISBN").optional(),
  workTitle: z.string().trim().min(1).max(300),
  contributorDisplay: z.string().trim().max(300).optional(),
  editionId: z.string().trim().min(1).optional(),
  variationId: z.string().trim().min(1).optional(),
  formatCode: z.string().trim().min(1).max(80).default("other"),
  productType: productTypeSchema.default("book"),
  trackingMode: trackingModeSchema.default("quantity"),
  quantity: z.number().int().positive().max(9999).default(1),
  unitCostAmount: z.number().int().min(0).default(0),
  unitSellingPriceAmount: z.number().int().min(0).default(0),
  currency: z.string().length(3).default("USD"),
  conditionCode: conditionCodeSchema.default("good"),
  conditionNotes: z.string().trim().max(1000).optional(),
  locationId: z.string().trim().min(1).default("loc-main"),
  shelfCode: z.string().trim().max(80).optional(),
  storeBarcodes: z.array(z.string().trim().min(1).max(120)).default([]),
  intendedLabelQuantity: z.number().int().min(0).max(9999).default(1),
  metadata: bookMetadataSchema.optional(),
});

export const phase4ReceivingBatchCreateSchema = z.object({
  targetLocationId: z.string().trim().min(1).default("loc-main"),
  receivingSource: z.string().trim().min(1).max(80).default("manual"),
  supplierName: z.string().trim().max(120).optional(),
  externalReference: z.string().trim().max(160).optional(),
  notes: z.string().trim().max(1000).optional(),
  idempotencyKey: z.string().min(8).optional(),
});

export const phase4ReceivingBatchUpdateSchema = phase4ReceivingBatchCreateSchema.partial();

export const phase4ReceivingLineSaveSchema = phase4ReceivingLineInputSchema.extend({
  lineId: z.string().trim().min(1).optional(),
});

export const phase4ReceivingCompleteSchema = z.object({
  idempotencyKey: z.string().min(8),
  prepareLabels: z.boolean().default(true),
});

export const receivingResolveSchema = z.object({
  input: z.string().trim().min(1).max(200),
});

export const receivingScannerSessionCreateSchema = z.object({
  expiresInMinutes: z.number().int().min(5).max(720).default(240),
});

export const receivingScannerScanCreateSchema = z.object({
  input: z.string().trim().min(1).max(200),
  idempotencyKey: z.string().trim().min(8).max(120).optional(),
});

export const receivingScannerScanAckSchema = z.object({
  status: z.enum(["processed", "needs_review", "failed"]),
  message: z.string().trim().max(500).optional(),
});

export const receivingCorrectionSchema = z.object({
  idempotencyKey: z.string().min(8),
  reason: z.string().trim().min(1).max(500),
  lineId: z.string().trim().min(1),
  quantityDelta: z.number().int().max(0).optional(),
  archiveCopyIds: z.array(z.string().trim().min(1)).default([]),
  labelOnly: z.boolean().default(false),
}).refine((value) => value.labelOnly || value.quantityDelta !== undefined || value.archiveCopyIds.length > 0, "Provide a correction action");

const labelFieldTokenSchema = z.enum(["store_logo", "store_name", "title", "contributor", "format", "condition", "price", "barcode", "inventory_number", "location", "shelf", "note"]);

export const labelTemplateDefinitionSchema = z.object({
  fields: z.array(labelFieldTokenSchema).min(1).max(12),
  barcodeSymbology: z.literal("code128").default("code128"),
  showHumanReadable: z.boolean().default(true),
}).strict();

export const labelTemplateSaveSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).optional(),
  active: z.boolean().default(true),
  pageWidthMm: z.number().min(20).max(1000),
  pageHeightMm: z.number().min(20).max(1000),
  labelWidthMm: z.number().min(10).max(300),
  labelHeightMm: z.number().min(5).max(300),
  marginTopMm: z.number().min(0).max(100).default(0),
  marginRightMm: z.number().min(0).max(100).default(0),
  marginBottomMm: z.number().min(0).max(100).default(0),
  marginLeftMm: z.number().min(0).max(100).default(0),
  gapXMm: z.number().min(0).max(100).default(0),
  gapYMm: z.number().min(0).max(100).default(0),
  rows: z.number().int().min(1).max(100),
  columns: z.number().int().min(1).max(20),
  orientation: z.enum(["portrait", "landscape"]).default("portrait"),
  definition: labelTemplateDefinitionSchema,
});

export const labelInventorySelectionSchema = z.object({
  copyId: z.string().trim().min(1).optional(),
  variationId: z.string().trim().min(1).optional(),
  locationId: z.string().trim().min(1).optional(),
}).refine((value) => Boolean(value.copyId) || Boolean(value.variationId && value.locationId), "Select a copy or quantity inventory position");

export const labelBatchCreateSchema = z.object({
  sourceType: z.enum(["receiving", "import", "inventory", "manual"]),
  sourceReceivingBatchId: z.string().trim().min(1).optional(),
  sourceImportJobId: z.string().trim().min(1).optional(),
  inventoryCopyIds: z.array(z.string().trim().min(1)).default([]),
  inventorySelections: z.array(labelInventorySelectionSchema).default([]),
  templateId: z.string().trim().min(1),
  idempotencyKey: z.string().min(8).optional(),
}).refine((value) => {
  if (value.sourceType === "receiving") return Boolean(value.sourceReceivingBatchId);
  if (value.sourceType === "import") return Boolean(value.sourceImportJobId);
  if (value.sourceType === "inventory") return value.inventoryCopyIds.length > 0 || value.inventorySelections.length > 0;
  return true;
}, "Provide the source records for this label batch");

export const labelInventorySearchSchema = z.object({
  q: z.string().trim().max(200).optional(),
  filter: z.enum(["recent", "week", "month", "all"]).default("recent"),
  locationId: z.string().trim().max(120).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const labelGenerateSchema = z.object({
  idempotencyKey: z.string().min(8),
  startingPosition: z.number().int().min(1).default(1),
});
